import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CheckSquare, ChevronDown, ChevronUp, Download } from 'lucide-react';
import html2canvas from 'html2canvas';
import { PDFDocument, PageSizes, degrees, rgb } from 'pdf-lib';
import fontkit from '@pdf-lib/fontkit';
import openSansRegular from 'open-sans-fonts/open-sans/Regular/OpenSans-Regular.ttf';
import openSansBold from 'open-sans-fonts/open-sans/Bold/OpenSans-Bold.ttf';
import authFetch from '../utils/authFetch';
import UniversalChecklistModal from '../components/UniversalChecklistModal';
import { SELLING_PROPERTY_TYPE_OPTIONS, SELLING_PROPERTY_REAL_ESTATE_TYPE_OPTIONS, normalizeSellingPropertyType } from '../utils/sellingPropertyTypes';
import { parseSellingPropertyCoordinates } from '../utils/sellingPropertyCoordinates';
import MasterParcelChecklistSection from '../components/MasterParcelChecklistSection';
import { resolveContactConflictResponse } from '../utils/contactResolution';
import {
  MASTER_PARCEL_TEMPLATE_PRESET,
  MPT_AREA_SECTION_ITEM_ID,
  MPT_ANALIZA_TRGA_ITEM_ID,
  MPT_GIS_IZPISI_ITEM_ID,
  MPT_IZRIS_E_PROSTOR_ITEM_ID,
  MPT_LOKACIJSKA_INFORMACIJA_ITEM_ID,
  MPT_OGLEDNICA_ITEM_ID,
  MPT_PREDSTAVITEV_PARCELE_ITEM_ID,
  MPT_ZK_ITEM_ID,
  createMasterParcelCustomFieldRenderer,
  isMasterParcelChecklistItemFilled,
  renderMasterParcelSummary
} from './sellingPropertyMpt';

const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
const API_BASE = isLocal ? 'http://localhost:5000' : 'https://bdinteldb.com';
const MULTI_DOCUMENT_CHECKLIST_ITEM_IDS = new Set([
  MPT_LOKACIJSKA_INFORMACIJA_ITEM_ID,
  MPT_IZRIS_E_PROSTOR_ITEM_ID,
  MPT_GIS_IZPISI_ITEM_ID,
  MPT_ANALIZA_TRGA_ITEM_ID,
  MPT_OGLEDNICA_ITEM_ID,
  MPT_PREDSTAVITEV_PARCELE_ITEM_ID
]);

const normalizeChecklistDocumentValue = (value) => {
  const documentName = String(value?.documentName || '').trim();
  const documentPath = String(value?.documentPath || '').trim();
  if (!documentName || !documentPath) {
    return null;
  }

  return { documentName, documentPath };
};

const isMultiDocumentChecklistItem = (item) => item?.type === 'document' && MULTI_DOCUMENT_CHECKLIST_ITEM_IDS.has(String(item.id || ''));

const getChecklistDocumentValues = (item, value) => {
  const documents = Array.isArray(value) ? value : [value];
  return documents.map((documentValue) => normalizeChecklistDocumentValue(documentValue)).filter(Boolean);
};

const sendWithContactResolution = async (makeRequest, initialPayload) => {
  let payload = initialPayload;

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const response = await makeRequest(payload);
    if (response.ok) {
      return response;
    }

    const conflict = await resolveContactConflictResponse(response, payload);
    if (!conflict.shouldRetry) {
      if (conflict.errorData) {
        response.copilotErrorData = conflict.errorData;
      }
      return response;
    }

    payload = conflict.payload;
  }

  return makeRequest(payload);
};

const getResponseErrorMessage = async (response, fallbackMessage) => {
  if (!response) return fallbackMessage;

  if (response.copilotErrorData?.error) {
    return response.copilotErrorData.error;
  }

  try {
    const rawText = await response.clone().text();
    if (!rawText) return fallbackMessage;

    try {
      const parsed = JSON.parse(rawText);
      return parsed.error || parsed.message || fallbackMessage;
    } catch (error) {
      return rawText;
    }
  } catch (error) {
    return fallbackMessage;
  }
};

const createEmptyShowingForm = () => ({
  showingDateTime: '',
  name: '',
  email: '',
  phone: '',
  notes: ''
});

const createEmptyContactForm = () => ({
  name: '',
  email: '',
  phone: '',
  notes: ''
});

const createEmptyPropertyNoteForm = () => ({
  note: ''
});

const createOwnerDraft = (name = '') => ({ name });

const SELLING_CHECKLIST_SCOPE = 'selling_property';
const SELLING_CHECKLIST_ENTITY_TYPE = 'selling_property';
const DOCUMENT_ACCEPT = '.pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.txt,.zip,.rar';

const ownerDraftsToText = (owners = []) => owners
  .map((owner) => String(owner?.name || '').trim())
  .filter(Boolean)
  .join('\n');

const ownerTextToDrafts = (value = '') => {
  const drafts = String(value || '')
    .split(/[\n,;]+/)
    .map((item) => item.trim())
    .filter(Boolean)
    .map((name) => createOwnerDraft(name));

  return drafts.length > 0 ? drafts : [createOwnerDraft()];
};

const formatEuro = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const num = Number(value);
  if (Number.isNaN(num)) return `${value} €`;
  return `${Math.round(num).toLocaleString('sl-SI')} €`;
};

const parseNumberValue = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(String(value).replace(',', '.'));
  return Number.isNaN(parsed) ? null : parsed;
};

const computePricePerSquareMeter = ({ area, price }) => {
  const parsedArea = parseNumberValue(area);
  const parsedPrice = parseNumberValue(price);
  if (parsedArea === null || parsedPrice === null || parsedArea === 0) return null;
  return parsedPrice / parsedArea;
};

const normalizeComparableText = (value) => String(value || '').trim().toLowerCase().replace(/\s+/g, ' ');

const isUnbuiltLandType = (value) => {
  const normalized = normalizeComparableText(value);
  return normalized === 'nepozidano stavbno zemljišče'
    || normalized === 'nepozidana zazidljiva parcela';
};

const computeCommissionValue = ({ value, type, base }) => {
  const parsedValue = parseNumberValue(value);
  if (parsedValue === null) return null;
  if (type === '%') {
    const parsedBase = parseNumberValue(base);
    if (parsedBase === null) return null;
    return (parsedValue / 100) * parsedBase;
  }
  return parsedValue;
};

const computePercentValue = ({ value, base }) => {
  const parsedValue = parseNumberValue(value);
  const parsedBase = parseNumberValue(base);
  if (parsedValue === null || parsedBase === null || parsedBase === 0) return null;
  return (parsedValue / parsedBase) * 100;
};

const isChecklistValueFilled = (item, value) => {
  if (!item) return false;
  if (item.type === 'checkbox') return value === true;
  if (item.type === 'document') return getChecklistDocumentValues(item, value).length > 0;
  return String(value ?? '').trim() !== '';
};

const formatChecklistValue = (item, value) => {
  if (item?.type === 'checkbox') {
    return value === true ? 'checked' : '';
  }

  if (item?.type === 'document') {
    return getChecklistDocumentValues(item, value).map((documentValue) => documentValue.documentName).join(', ');
  }

  return String(value ?? '').trim();
};

const CHECKLIST_EXPORTABLE_MPT_TYPES = new Set([
  'mpt_zk_extracts',
  'mpt_access',
  'mpt_neighbor_parcels',
  'mpt_electric_network',
  'mpt_water_network',
  'mpt_gurs_izpisi',
  'mpt_vrednotenje_izpisi',
  'mpt_ostali_dokumenti'
]);

const isChecklistDocumentCapable = (item) => {
  if (!item) return false;
  if (item.type === 'document') return true;
  return CHECKLIST_EXPORTABLE_MPT_TYPES.has(String(item.type || ''));
};

const createZkPdfSource = ({ zkExtractId, ko, idParceleStavbe, label = '', history = false }) => {
  if (!zkExtractId) return null;

  const trimmedKo = String(ko || '').trim();
  const trimmedParcelId = String(idParceleStavbe || '').trim();
  const fallbackName = [trimmedKo, trimmedParcelId].filter(Boolean).join('_');

  return {
    kind: 'zk',
    zkExtractId,
    ko: trimmedKo,
    idParceleStavbe: trimmedParcelId,
    history,
    documentName: label || `${history ? 'ZK_History' : 'ZK_Extract'}_${fallbackName || zkExtractId}.pdf`
  };
};

const createDocumentPdfSource = (documentValue) => documentValue?.documentPath
  ? {
      kind: 'document',
      ...documentValue
    }
  : null;

const getChecklistItemDocumentValues = (item, value, propertyForm) => {
  if (!item) return [];

  if (item.type === 'document') {
    return getChecklistDocumentValues(item, value).map((documentValue) => createDocumentPdfSource(documentValue)).filter((documentValue) => documentValue?.documentPath);
  }

  if (item.type === 'mpt_zk_extracts') {
    const sources = [];
    const primarySource = createZkPdfSource({
      zkExtractId: propertyForm?.zkExtractId,
      ko: propertyForm?.katastrskaObcina,
      idParceleStavbe: propertyForm?.idParceleStavbe,
      label: `ZK extract - parcela 1${propertyForm?.katastrskaObcina || propertyForm?.idParceleStavbe ? ` (${[propertyForm?.katastrskaObcina, propertyForm?.idParceleStavbe].filter(Boolean).join(' ')})` : ''}`
    });
    const primaryHistorySource = createZkPdfSource({
      zkExtractId: propertyForm?.zkExtractId,
      ko: propertyForm?.katastrskaObcina,
      idParceleStavbe: propertyForm?.idParceleStavbe,
      label: `ZK history - parcela 1${propertyForm?.katastrskaObcina || propertyForm?.idParceleStavbe ? ` (${[propertyForm?.katastrskaObcina, propertyForm?.idParceleStavbe].filter(Boolean).join(' ')})` : ''}`,
      history: true
    });

    if (primarySource) {
      sources.push(primarySource);
    }
    if (primaryHistorySource) {
      sources.push(primaryHistorySource);
    }

    if (Array.isArray(value?.extraParcels)) {
      value.extraParcels.forEach((parcel, index) => {
        const source = createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK extract - parcela ${index + 2}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`
        });

        if (source) {
          sources.push(source);
        }

        const historySource = createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK history - parcela ${index + 2}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`,
          history: true
        });

        if (historySource) {
          sources.push(historySource);
        }
      });
    }

    return sources;
  }

  if (item.type === 'mpt_access') {
    if (!value) return [];
    const sources = [];

    const municipalSource = createZkPdfSource({
      zkExtractId: value?.municipalZkExtractId,
      ko: value?.ko,
      idParceleStavbe: value?.parcela,
      label: `ZK extract - dostop${value?.ko || value?.parcela ? ` (${[value?.ko, value?.parcela].filter(Boolean).join(' ')})` : ''}`
    });

    if (municipalSource) {
      sources.push(municipalSource);
    }

    if (Array.isArray(value?.parcels)) {
      value.parcels.forEach((parcel, index) => {
        const source = createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK extract - dostop parcela ${index + 1}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`
        });

        if (source) {
          sources.push(source);
        }
      });
    }

    return sources;
  }

  if (item.type === 'mpt_neighbor_parcels') {
    if (!value) return [];
    return Array.isArray(value?.parcels)
      ? value.parcels
        .map((parcel, index) => createZkPdfSource({
          zkExtractId: parcel?.zkExtractId,
          ko: parcel?.ko,
          idParceleStavbe: parcel?.parcela,
          label: `ZK extract - sosednja parcela ${index + 1}${parcel?.ko || parcel?.parcela ? ` (${[parcel?.ko, parcel?.parcela].filter(Boolean).join(' ')})` : ''}`
        }))
        .filter(Boolean)
      : [];
  }

  if (item.type === 'mpt_electric_network' || item.type === 'mpt_water_network') {
    if (!value) return [];
    const locationDocuments = Array.isArray(value?.locationDocuments)
      ? value.locationDocuments
      : value?.locationDocument?.documentPath
        ? [value.locationDocument]
        : [];
    return locationDocuments.map((documentValue) => createDocumentPdfSource(documentValue)).filter((documentValue) => documentValue?.documentPath);
  }

  if (item.type === 'mpt_gurs_izpisi' || item.type === 'mpt_vrednotenje_izpisi') {
    if (!value) return [];
    return Array.isArray(value?.documents)
      ? value.documents.flatMap((entry) => {
          const documents = Array.isArray(entry?.documents)
            ? entry.documents
            : entry?.document?.documentPath
              ? [entry.document]
              : [];
          return documents.map((documentValue) => createDocumentPdfSource(documentValue)).filter((documentValue) => documentValue?.documentPath);
        })
      : [];
  }

  if (item.type === 'mpt_ostali_dokumenti') {
    if (!value) return [];
    return Array.isArray(value?.documents)
      ? value.documents.map((entry) => createDocumentPdfSource(entry?.document)).filter((documentValue) => documentValue?.documentPath)
      : [];
  }

  return [];
};

const isPdfDocumentValue = (documentValue) => {
  if (documentValue?.kind === 'zk') {
    return true;
  }

  return /\.pdf$/i.test(String(documentValue?.documentName || documentValue?.documentPath || '').trim());
};

const downloadArrayBufferAsFile = (arrayBuffer, filename) => {
  const blob = new Blob([arrayBuffer], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
};

const splitTextToLines = (text, maxCharactersPerLine = 64) => {
  const normalizedText = String(text || '').trim();
  if (!normalizedText) {
    return [];
  }

  const words = normalizedText.split(/\s+/);
  const lines = [];
  let currentLine = '';

  words.forEach((word) => {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (candidate.length <= maxCharactersPerLine) {
      currentLine = candidate;
      return;
    }

    if (currentLine) {
      lines.push(currentLine);
    }

    currentLine = word;
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

let cachedPdfFontPromise = null;

const loadPdfFonts = async (pdfDocument) => {
  if (!cachedPdfFontPromise) {
    cachedPdfFontPromise = Promise.all([
      fetch(openSansRegular).then((response) => response.arrayBuffer()),
      fetch(openSansBold).then((response) => response.arrayBuffer())
    ]);
  }

  const [regularFontBytes, boldFontBytes] = await cachedPdfFontPromise;
  pdfDocument.registerFontkit(fontkit);

  return {
    regularFont: await pdfDocument.embedFont(regularFontBytes),
    boldFont: await pdfDocument.embedFont(boldFontBytes)
  };
};

const waitForNextPaint = () => new Promise((resolve) => {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(resolve);
  });
});

const wrapTextToWidth = (text, font, size, maxWidth) => {
  const normalizedText = String(text || '');
  if (!normalizedText.trim()) {
    return [''];
  }

  const paragraphs = normalizedText.split('\n');
  const lines = [];

  paragraphs.forEach((paragraph) => {
    const trimmedParagraph = paragraph.trim();
    if (!trimmedParagraph) {
      lines.push('');
      return;
    }

    const words = trimmedParagraph.split(/\s+/);
    let currentLine = '';

    words.forEach((word) => {
      const candidate = currentLine ? `${currentLine} ${word}` : word;
      if (font.widthOfTextAtSize(candidate, size) <= maxWidth) {
        currentLine = candidate;
        return;
      }

      if (currentLine) {
        lines.push(currentLine);
      }

      currentLine = word;
    });

    if (currentLine) {
      lines.push(currentLine);
    }
  });

  return lines;
};

const createExportParcelList = (propertyForm, zkValue) => {
  const extraParcels = Array.isArray(zkValue?.extraParcels)
    ? zkValue.extraParcels.filter((parcel) => parcel && (parcel.ko || parcel.parcela || parcel.zkExtractId || parcel.ownerNames))
    : [];

  return [
    {
      label: 'parcela 1',
      ko: String(propertyForm?.katastrskaObcina || '').trim(),
      parcela: String(propertyForm?.idParceleStavbe || '').trim(),
      zkExtractId: propertyForm?.zkExtractId || null,
      ownerNames: ''
    },
    ...extraParcels.map((parcel, index) => ({
      label: `parcela ${index + 2}`,
      ko: String(parcel?.ko || '').trim(),
      parcela: String(parcel?.parcela || '').trim(),
      zkExtractId: Number.isInteger(Number(parcel?.zkExtractId)) ? Number(parcel.zkExtractId) : null,
      ownerNames: String(parcel?.ownerNames || '').trim()
    }))
  ].filter((parcel) => parcel.ko || parcel.parcela || parcel.zkExtractId || parcel.ownerNames);
};

const getChecklistExportLines = ({ item, value, propertyForm, checklistValues }) => {
  if (!item) {
    return [];
  }

  if (item.type === 'checkbox') {
    return value === true ? ['Oznaceno'] : [];
  }

  if (item.type === 'document') {
    return getChecklistDocumentValues(item, value).map((documentValue) => documentValue.documentName);
  }

  if (item.type === 'mpt_zk_extracts') {
    return createExportParcelList(propertyForm, value).flatMap((parcel) => {
      const lines = [`${parcel.label}: ${[parcel.ko, parcel.parcela].filter(Boolean).join(' ').trim() || 'Ni doloceno'}`];
      if (parcel.ownerNames) lines.push(`Lastniki: ${parcel.ownerNames}`);
      if (parcel.zkExtractId) lines.push('ZK izpisek je na voljo');
      return lines;
    });
  }

  if (item.type === 'mpt_area_section') {
    const area = String(value?.area || '').trim();
    const minHeight = String(value?.minHeight || '').trim();
    const maxHeight = String(value?.maxHeight || '').trim();
    const minSlope = String(value?.minSlope || '').trim();
    const maxSlope = String(value?.maxSlope || '').trim();
    const parsedMinHeight = parseNumberValue(minHeight);
    const parsedMaxHeight = parseNumberValue(maxHeight);
    const heightDifference = parsedMinHeight === null || parsedMaxHeight === null ? null : parsedMaxHeight - parsedMinHeight;
    const pricePerSquareMeter = computePricePerSquareMeter({ area, price: propertyForm?.price });
    const lines = [];
    if (area) lines.push(`Povrsina: ${area} m2`);
    if (pricePerSquareMeter !== null && pricePerSquareMeter !== undefined) lines.push(`Cena / m2: ${formatEuro(pricePerSquareMeter)}`);
    if (minHeight) lines.push(`Min visina [m]: ${minHeight}`);
    if (maxHeight) lines.push(`Max visina [m]: ${maxHeight}`);
    if (heightDifference !== null) lines.push(`Razlika v visini: ${heightDifference}`);
    if (maxSlope) lines.push(`Max naklon [°]: ${maxSlope}`);
    if (minSlope) lines.push(`Min naklon [°]: ${minSlope}`);
    return lines;
  }

  if (item.type === 'mpt_access') {
    const mode = String(value?.mode || '').trim();
    const lines = [];
    if (mode) lines.push(`Status: ${mode}`);
    if (mode === 'dostop ni urejen' && value?.notes) lines.push(`Opombe: ${value.notes}`);
    if (mode === 'iz občinske parcele') {
      const parcelText = [String(value?.ko || '').trim(), String(value?.parcela || '').trim()].filter(Boolean).join(' ');
      if (parcelText) lines.push(`Obcinska parcela: ${parcelText}`);
      if (value?.municipalOwnerNames) lines.push(`Lastniki: ${value.municipalOwnerNames}`);
      if (value?.municipalNotes) lines.push(`Opombe: ${value.municipalNotes}`);
    }
    if (mode === 'Preko parcel' && Array.isArray(value?.parcels)) {
      value.parcels.forEach((parcel, index) => {
        const parcelText = [String(parcel?.ko || '').trim(), String(parcel?.parcela || '').trim()].filter(Boolean).join(' ');
        if (parcelText) lines.push(`Parcela ${index + 1}: ${parcelText}`);
        if (parcel?.ownerNames) lines.push(`Lastniki parcele ${index + 1}: ${parcel.ownerNames}`);
        if (parcel?.notes) lines.push(`Opombe parcele ${index + 1}: ${parcel.notes}`);
      });
    }
    return lines;
  }

  if (item.type === 'mpt_neighbor_parcels') {
    return Array.isArray(value?.parcels)
      ? value.parcels.flatMap((parcel, index) => {
          const lines = [];
          const parcelText = [String(parcel?.ko || '').trim(), String(parcel?.parcela || '').trim()].filter(Boolean).join(' ');
          if (parcelText) lines.push(`Parcela ${index + 1}: ${parcelText}`);
          if (parcel?.ownerNames) lines.push(`Lastniki parcele ${index + 1}: ${parcel.ownerNames}`);
          if (parcel?.notes) lines.push(`Opombe parcele ${index + 1}: ${parcel.notes}`);
          return lines;
        })
      : [];
  }

  if (item.type === 'mpt_komunalni_prispevek') {
    const lines = [];
    const status = String(value?.status || '').trim();
    if (status) lines.push(`Status: ${status}`);
    if (value?.house100) lines.push(`Za 100 m2 hiso: ${value.house100}`);
    if (value?.house150) lines.push(`Za 150 m2 hiso: ${value.house150}`);
    if (value?.customLabel || value?.customValue) lines.push(`${value?.customLabel || 'Custom'}: ${value?.customValue || 'Ni doloceno'}`);
    return lines;
  }

  if (item.type === 'mpt_electric_network' || item.type === 'mpt_water_network') {
    const lines = [];
    if (value?.hasConnection === true) lines.push('Ima prikljucek');
    const locationDocuments = Array.isArray(value?.locationDocuments)
      ? value.locationDocuments
      : value?.locationDocument?.documentName
        ? [value.locationDocument]
        : [];
    if (value?.hasConnection !== true && (value?.manager || value?.location || locationDocuments.length > 0 || value?.notes)) lines.push('Nima prikljucka');
    if (item.type === 'mpt_electric_network' && value?.connectionPower) lines.push(`Prikljucna moc: ${value.connectionPower}`);
    if (item.type === 'mpt_electric_network' && value?.phase) lines.push(`Faza: ${value.phase}`);
    if (value?.manager) lines.push(`Upravitelj: ${value.manager}`);
    if (value?.location) lines.push(`Lokacija: ${value.location}`);
    locationDocuments.forEach((documentValue) => {
      if (documentValue?.documentName) {
        lines.push(`Dokument: ${documentValue.documentName}`);
      }
    });
    if (value?.notes) lines.push(`Opombe: ${value.notes}`);
    return lines;
  }

  if (item.type === 'mpt_sewer_network') {
    const lines = [];
    if (value?.isNearby === true) lines.push('Je v blizini');
    if (value?.isNearby === false && (value?.location || value?.notes)) lines.push('Ni v blizini');
    if (value?.location) lines.push(`Lokacija: ${value.location}`);
    if (value?.notes) lines.push(`Opombe: ${value.notes}`);
    return lines;
  }

  if (item.type === 'mpt_urejene_meje') {
    const lines = [];
    if (value?.isChecked === true || value === true) lines.push('Urejene meje');
    if (value?.notes) lines.push(`Opombe: ${value.notes}`);
    return lines;
  }

  if (item.type === 'mpt_gurs_izpisi' || item.type === 'mpt_vrednotenje_izpisi') {
    return Array.isArray(value?.documents)
      ? value.documents.flatMap((entry) => {
          const parcelLabel = [String(entry?.ko || '').trim(), String(entry?.parcela || '').trim()].filter(Boolean).join(' ');
          const documents = Array.isArray(entry?.documents)
            ? entry.documents
            : entry?.document?.documentName || entry?.document?.documentPath
              ? [entry.document]
              : [];
          return documents
            .filter((documentValue) => documentValue?.documentName || documentValue?.documentPath)
            .map((documentValue) => `${parcelLabel}: ${documentValue?.documentName || 'Dokument'}`);
        })
      : [];
  }

  if (item.type === 'mpt_linki') {
    const lines = [];
    if (value?.website) lines.push(`Oglas spletna stran: ${value.website}`);
    if (value?.nepremicnine) lines.push(`Oglas nepremicnine.net: ${value.nepremicnine}`);
    if (value?.video) lines.push(`Video: ${value.video}`);
    return lines;
  }

  if (item.type === 'mpt_ostali_dokumenti') {
    return Array.isArray(value?.documents)
      ? value.documents
        .filter((entry) => entry?.name || entry?.document?.documentName)
        .map((entry) => `${entry?.name || 'Dokument'}${entry?.document?.documentName ? `: ${entry.document.documentName}` : ''}`)
      : [];
  }

  return formatChecklistValue(item, value) ? [formatChecklistValue(item, value)] : [];
};

const canvasToDataUrl = (canvas) => canvas.toDataURL('image/png');

const addChecklistSummaryFromDomToPdf = async (mergedPdf, element, options = {}) => {
  if (!element) {
    throw new Error('Could not render checklist export.');
  }

  await waitForNextPaint();

  const itemCount = element.querySelectorAll('.checklist-export-item').length;
  const bounds = element.getBoundingClientRect();
  const hasMeaningfulSize = bounds.width > 0 && bounds.height > 0;

  if (!hasMeaningfulSize) {
    throw new Error('Checklist export surface was not rendered before capture.');
  }

  if (itemCount === 0) {
    throw new Error('Checklist export surface has no checklist items to capture.');
  }
  const [a4Width, a4Height] = PageSizes.A4;
  const overlayLabel = String(options.overlayLabel || '').trim();
  const overlayEnabled = options.overlayEnabled === true && overlayLabel;
  const overlayFont = options.overlayFont || null;
  const marginX = 24;
  const topMargin = 24;
  const bottomMargin = 24;
  const contentWidth = a4Width - marginX * 2;
  const itemGap = 10;
  const maxPageContentHeight = a4Height - topMargin - bottomMargin;
  let page = null;
  let cursorY = 0;

  const drawOverlay = (targetPage) => {
    if (overlayEnabled && overlayFont) {
      targetPage.drawText(overlayLabel, {
        x: 24,
        y: a4Height - 20,
        size: 10,
        font: overlayFont,
        color: rgb(0.2, 0.24, 0.31),
        opacity: 0.82
      });
    }
  };

  const startPage = () => {
    page = mergedPdf.addPage(PageSizes.A4);
    drawOverlay(page);
    cursorY = a4Height - topMargin;
  };

  const itemElements = Array.from(element.querySelectorAll('.checklist-export-item'));
  startPage();

  for (const itemElement of itemElements) {
    const itemCanvas = await html2canvas(itemElement, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      logging: false,
      windowWidth: Math.max(document.documentElement.clientWidth, itemElement.scrollWidth || 0, 1200),
      windowHeight: Math.max(document.documentElement.clientHeight, itemElement.scrollHeight || 0, 1200)
    });

    if (!itemCanvas.width || !itemCanvas.height) {
      continue;
    }

    const fullRenderedHeight = (itemCanvas.height / itemCanvas.width) * contentWidth;
    const availableHeightBeforeItem = cursorY - bottomMargin;

    if (fullRenderedHeight <= maxPageContentHeight && fullRenderedHeight > availableHeightBeforeItem) {
      startPage();
    }

    let remainingTop = 0;
    while (remainingTop < itemCanvas.height) {
      if (!page) {
        startPage();
      }

      const availableHeight = cursorY - bottomMargin;
      if (availableHeight <= 24) {
        startPage();
      }

      const recalculatedAvailableHeight = cursorY - bottomMargin;
      const maxSliceHeightPx = Math.max(1, Math.floor(itemCanvas.width * (recalculatedAvailableHeight / contentWidth)));
      const sliceHeightPx = Math.min(itemCanvas.height - remainingTop, maxSliceHeightPx);
      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = itemCanvas.width;
      sliceCanvas.height = sliceHeightPx;

      const sliceContext = sliceCanvas.getContext('2d');
      if (!sliceContext) {
        throw new Error('Could not prepare checklist item canvas slice.');
      }

      sliceContext.fillStyle = '#ffffff';
      sliceContext.fillRect(0, 0, sliceCanvas.width, sliceCanvas.height);
      sliceContext.drawImage(
        itemCanvas,
        0,
        remainingTop,
        itemCanvas.width,
        sliceHeightPx,
        0,
        0,
        itemCanvas.width,
        sliceHeightPx
      );

      const embeddedImage = await mergedPdf.embedPng(canvasToDataUrl(sliceCanvas));
      const renderedHeight = (sliceHeightPx / itemCanvas.width) * contentWidth;

      page.drawImage(embeddedImage, {
        x: marginX,
        y: cursorY - renderedHeight,
        width: contentWidth,
        height: renderedHeight
      });

      cursorY -= renderedHeight;
      remainingTop += sliceHeightPx;

      if (remainingTop < itemCanvas.height) {
        startPage();
      } else {
        cursorY -= itemGap;
      }
    }
  }
};

const addPdfCoverPage = async (mergedPdf, { propertyForm, parcelNumbers, parcelArea, pricePerSquareMeter, regularFont, boldFont }) => {
  const page = mergedPdf.addPage(PageSizes.A4);
  const [pageWidth, pageHeight] = PageSizes.A4;
  const centeredText = (text, y, options = {}) => {
    const font = options.font || regularFont;
    const size = options.size || 12;
    const renderedText = String(text || '');
    const width = font.widthOfTextAtSize(renderedText, size);
    page.drawText(renderedText, {
      x: (pageWidth - width) / 2,
      y,
      font,
      size,
      color: options.color || rgb(0.08, 0.1, 0.14)
    });
  };

  centeredText('Dokumentacija zemljišča', pageHeight - 110, {
    font: boldFont,
    size: 24
  });

  const koNumber = String(propertyForm?.katastrskaObcina || '').trim() || 'Ni določeno';
  const koName = String(propertyForm?.koName || '').trim();
  const normalizedParcelNumbers = Array.isArray(parcelNumbers) ? parcelNumbers.filter(Boolean) : [];
  const parcelsText = normalizedParcelNumbers.length > 0 ? normalizedParcelNumbers.join(', ') : (String(propertyForm?.idParceleStavbe || '').trim() || 'Ni določeno');
  const areaText = parcelArea ? `${parcelArea} m²` : 'Ni določeno';
  const hasPrice = String(propertyForm?.price ?? '').trim() !== '';

  const detailLines = [
    `KO: ${koNumber}`,
    `${normalizedParcelNumbers.length <= 1 ? 'Parcela' : 'Parcele'}: ${parcelsText}`,
    `Velikost: ${areaText}`
  ];

  if (koName) {
    detailLines.splice(1, 0, `Ime KO: ${koName}`);
  }

  if (hasPrice) {
    detailLines.push(`Cena: ${formatEuro(propertyForm?.price)}`);
    if (pricePerSquareMeter !== null && pricePerSquareMeter !== undefined) {
      detailLines.push(`Cena / m²: ${formatEuro(pricePerSquareMeter)}`);
    }
  }

  detailLines.forEach((line, index) => {
    centeredText(line, pageHeight - 180 - index * 28, {
      size: 14,
      color: rgb(0.2, 0.24, 0.31)
    });
  });

  const footerText = `Ustvaril Bernard Dobre ${new Date().toLocaleDateString('sl-SI')}`;
  const footerWidth = regularFont.widthOfTextAtSize(footerText, 10);
  page.drawText(footerText, {
    x: pageWidth - footerWidth - 28,
    y: 26,
    font: regularFont,
    size: 10,
    color: rgb(0.38, 0.43, 0.5)
  });
};

const appendPdfToDocument = async (mergedPdf, pdfBytes, options = {}) => {
  const sourcePdf = await PDFDocument.load(pdfBytes);
  const overlayLabel = String(options.overlayLabel || '').trim();
  const overlayEnabled = options.overlayEnabled === true && overlayLabel;
  const overlayFont = options.overlayFont || null;

  const copiedPages = await mergedPdf.copyPages(sourcePdf, sourcePdf.getPageIndices());
  copiedPages.forEach((page) => {
    mergedPdf.addPage(page);

    if (overlayEnabled && overlayFont) {
      const { height } = page.getSize();
      page.drawText(overlayLabel, {
        x: 24,
        y: height - 20,
        size: 10,
        font: overlayFont,
        color: rgb(0.2, 0.24, 0.31),
        opacity: 0.82
      });
    }
  });
};

const formatPercent = (value) => {
  if (value === null || value === undefined || value === '') return '';
  const parsed = Number(value);
  if (Number.isNaN(parsed)) return '';
  return `${parsed.toFixed(2)} %`;
};

const formatDateTime = (value) => (value ? new Date(value).toLocaleString() : '');

const copyToClipboard = async (value, errorMessage) => {
  if (!String(value || '').trim()) return;

  try {
    await navigator.clipboard.writeText(String(value));
  } catch (error) {
    console.error(errorMessage, error);
    window.alert('Could not copy value.');
  }
};

const toDateTimeLocalValue = (value) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

const activityFieldLabels = {
  katastrskaObcina: 'Katastrska obcina',
  idParceleStavbe: 'ID parcele / stavbe',
  type: 'Tip',
  ownersText: 'Owners',
  buyersText: 'Buyers',
  source: 'Vir',
  location: 'Lokacija',
  price: 'Cena',
  grossCommission: 'Bruto provizija',
  netCommission: 'Neto provizija',
  grossCommissionInput: 'Bruto vnos',
  grossCommissionInputType: 'Bruto tip vnosa',
  netCommissionInput: 'Neto vnos',
  netCommissionInputType: 'Neto tip vnosa',
  createdAt: 'Podpis posr. pogodbe',
  status: 'Status',
  showingDateTime: 'Termin ogleda',
  name: 'Ime',
  email: 'Email',
  phone: 'Telefon',
  notes: 'Opombe'
};

const MPT_PRESET_ITEM_MAP = new Map(MASTER_PARCEL_TEMPLATE_PRESET.items.map((item) => [item.id, item]));

const normalizeChecklistTemplateItems = (items) => Array.isArray(items)
  ? items.map((item) => {
      const presetItem = MPT_PRESET_ITEM_MAP.get(item?.id);
      if (!presetItem) {
        return item;
      }

      return {
        ...item,
        label: presetItem.label,
        type: presetItem.type,
        options: presetItem.options || item.options
      };
    })
  : [];

const formatActivityValue = (value) => {
  if (value === null || value === undefined || value === '') return 'Empty';
  try {
    const parsed = JSON.parse(value);
    if (Array.isArray(parsed)) {
      return parsed.map((item) => {
        if (item && typeof item === 'object') {
          return Object.entries(item).map(([key, itemValue]) => `${key}: ${itemValue}`).join(', ');
        }
        return String(item);
      }).join(', ');
    }
    if (parsed && typeof parsed === 'object') {
      return Object.entries(parsed).map(([key, itemValue]) => `${key}: ${itemValue}`).join(', ');
    }
  } catch (err) {
    return String(value);
  }

  return String(value);
};

const cardLinkButtonStyle = {
  padding: '6px 10px',
  backgroundColor: '#e0f2fe',
  color: '#075985',
  border: '1px solid #bae6fd',
  borderRadius: '999px',
  cursor: 'pointer',
  fontSize: '12px',
  fontWeight: '600'
};

const sectionCardStyle = {
  borderRadius: '16px',
  padding: '18px',
  border: '1px solid #e2e8f0',
  backgroundColor: 'white',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.08)'
};

const getDetailFieldStyle = (isEditing) => ({
  width: '100%',
  minWidth: 0,
  padding: '10px',
  borderRadius: '10px',
  border: '1px solid #cbd5e1',
  backgroundColor: isEditing ? 'white' : '#f8fafc',
  color: '#0f172a'
});

const detailValueStyle = {
  minHeight: '42px',
  display: 'flex',
  alignItems: 'center',
  color: '#0f172a',
  lineHeight: 1.5,
  wordBreak: 'break-word'
};

const emptyDetailValueStyle = {
  ...detailValueStyle,
  color: '#94a3b8'
};

const modalBackdropStyle = {
  position: 'fixed',
  inset: 0,
  backgroundColor: 'rgba(15, 23, 42, 0.48)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2000,
  padding: '20px'
};

const modalCardStyle = {
  width: '100%',
  maxWidth: '520px',
  backgroundColor: 'white',
  borderRadius: '16px',
  padding: '22px',
  boxShadow: '0 20px 25px -5px rgba(15, 23, 42, 0.25)'
};

const SellingPropertyPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const checklistExportRef = useRef(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isEditingProperty, setIsEditingProperty] = useState(false);
  const [savingProperty, setSavingProperty] = useState(false);
  const [savingShowing, setSavingShowing] = useState(false);
  const [savingContact, setSavingContact] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [propertyForm, setPropertyForm] = useState(null);
  const [propertyOwners, setPropertyOwners] = useState([]);
  const [showings, setShowings] = useState([]);
  const [propertyContacts, setPropertyContacts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [checklistExpanded, setChecklistExpanded] = useState(false);
  const [checklistTypeKey, setChecklistTypeKey] = useState('');
  const [checklistTemplateItems, setChecklistTemplateItems] = useState([]);
  const [checklistValues, setChecklistValues] = useState({});
  const [checklistLoading, setChecklistLoading] = useState(false);
  const [checklistSaving, setChecklistSaving] = useState(false);
  const [showChecklistExportModal, setShowChecklistExportModal] = useState(false);
  const [checklistExportSelection, setChecklistExportSelection] = useState([]);
  const [checklistExportIncludeCoverPage, setChecklistExportIncludeCoverPage] = useState(true);
  const [checklistExportIncludeItemName, setChecklistExportIncludeItemName] = useState(false);
  const [checklistExporting, setChecklistExporting] = useState(false);
  const [checklistExportSurfaceVisible, setChecklistExportSurfaceVisible] = useState(false);
  const [hasExistingMasterParcelChecklist, setHasExistingMasterParcelChecklist] = useState(false);
  const [hasAddedMasterParcelChecklist, setHasAddedMasterParcelChecklist] = useState(false);
  const [showAddShowingModal, setShowAddShowingModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [showMarkSoldModal, setShowMarkSoldModal] = useState(false);
  const [showingForm, setShowingForm] = useState(createEmptyShowingForm());
  const [contactForm, setContactForm] = useState(createEmptyContactForm());
  const [propertyNoteForm, setPropertyNoteForm] = useState(createEmptyPropertyNoteForm());
  const [markSoldForm, setMarkSoldForm] = useState({
    price: '',
    grossCommission: '',
    netCommission: '',
    grossCommissionType: '€',
    netCommissionType: '€',
    grossCommissionInput: '',
    netCommissionInput: '',
    buyersText: ''
  });
  const hasMasterParcelChecklist = isUnbuiltLandType(propertyForm?.type) || hasExistingMasterParcelChecklist || hasAddedMasterParcelChecklist;

  const filledChecklistItems = useMemo(() => checklistTemplateItems
    .map((item) => ({
      item,
      value: checklistValues[item.id]
    }))
    .filter(({ item, value }) => {
      if (String(item.type || '').startsWith('mpt_')) {
        return isMasterParcelChecklistItemFilled({
          item,
          value,
          price: propertyForm?.price,
          allValues: checklistValues
        });
      }

      return isChecklistValueFilled(item, value);
    }), [checklistTemplateItems, checklistValues, propertyForm?.price]);

  const checklistExportOptions = useMemo(() => {
    const options = [{
      id: 'checklist',
      label: 'Splošne informacije',
      type: 'checklist',
      enabled: Boolean(checklistTypeKey) && filledChecklistItems.length > 0,
      pdfDocuments: [],
      totalDocuments: 0
    }];

    checklistTemplateItems
      .filter((item) => isChecklistDocumentCapable(item))
      .forEach((item) => {
        const documentValues = getChecklistItemDocumentValues(item, checklistValues[item.id], propertyForm);
        const pdfDocuments = documentValues.filter((documentValue) => isPdfDocumentValue(documentValue));

        options.push({
          id: item.id,
          label: item.label,
          type: 'documents',
          enabled: pdfDocuments.length > 0,
          pdfDocuments,
          totalDocuments: documentValues.length
        });
      });

    return options;
  }, [checklistTemplateItems, checklistTypeKey, checklistValues, propertyForm, filledChecklistItems.length]);

  const loadChecklistData = useCallback(async (nextPropertyId, nextTypeKey) => {
    if (!nextPropertyId || !nextTypeKey) {
      setChecklistTypeKey('');
      setChecklistTemplateItems([]);
      setChecklistValues({});
      return;
    }

    setChecklistLoading(true);
    setChecklistTypeKey(nextTypeKey);

    try {
      const params = new URLSearchParams({
        scope: SELLING_CHECKLIST_SCOPE,
        typeKey: nextTypeKey,
        entityType: SELLING_CHECKLIST_ENTITY_TYPE,
        entityId: String(nextPropertyId)
      });
      const response = await authFetch(`${API_BASE}/api/checklists/entry?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch checklist');
      }

      const data = await response.json();
      setChecklistTemplateItems(Array.isArray(data.items) ? data.items : []);
      setChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
    } catch (err) {
      console.error(err);
      setChecklistTemplateItems([]);
      setChecklistValues({});
    } finally {
      setChecklistLoading(false);
    }
  }, []);

  const loadMasterParcelChecklistState = useCallback(async (nextPropertyId) => {
    if (!nextPropertyId) {
      setHasExistingMasterParcelChecklist(false);
      return;
    }

    try {
      const params = new URLSearchParams({
        scope: 'selling_property_mpt',
        typeKey: 'mpt',
        entityType: SELLING_CHECKLIST_ENTITY_TYPE,
        entityId: String(nextPropertyId)
      });
      const response = await authFetch(`${API_BASE}/api/checklists/entry?${params.toString()}`);
      if (!response.ok) {
        throw new Error('Failed to fetch MPT checklist');
      }

      const data = await response.json();
      const values = data.values && typeof data.values === 'object' ? data.values : {};
      setHasExistingMasterParcelChecklist(Object.keys(values).length > 0);
    } catch (err) {
      console.error(err);
      setHasExistingMasterParcelChecklist(false);
    }
  }, []);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/${id}`);
      if (!response.ok) {
        throw new Error('Failed to load property');
      }

      const data = await response.json();
      setPropertyForm({
        id: data.property.id,
        leadId: data.property.leadId,
        katastrskaObcina: data.property.katastrskaObcina || '',
        koName: data.property.koName || '',
        idParceleStavbe: data.property.idParceleStavbe || '',
        type: data.property.type || '',
        ownersText: data.property.ownersText || data.property.ownerNames || '',
        buyersText: data.property.buyersText || '',
        source: data.property.source || '',
        location: data.property.location || '',
        coordinates: data.property.coordinates || '',
        inCooperationWith: data.property.inCooperationWith || '',
        propertyType: data.property.propertyType || '',
        surfaceArea: data.property.surfaceArea ?? '',
        advertisingEnabled: data.property.advertisingEnabled !== false,
        adLink: data.property.adLink || '',
        presentationDocumentName: data.property.presentationDocumentName || '',
        presentationDocumentPath: data.property.presentationDocumentPath || '',
        price: data.property.price ?? '',
        grossCommission: data.property.grossCommission ?? '',
        netCommission: data.property.netCommission ?? '',
        grossCommissionInput: data.property.grossCommissionInput ?? '',
        grossCommissionType: data.property.grossCommissionInputType || '€',
        netCommissionInput: data.property.netCommissionInput ?? '',
        netCommissionType: data.property.netCommissionInputType || '€',
        zkExtractId: data.property.zkExtractId || null,
        status: data.property.status || 'selling',
        soldAt: data.property.soldAt || null,
        createdAt: data.property.createdAt || null
      });
      setPropertyOwners(data.property.owners || []);
      setShowings(data.showings || []);
      setPropertyContacts(data.contacts || []);
      setActivity(data.activity || []);
      await loadChecklistData(data.property.id, data.property.type || '');
      await loadMasterParcelChecklistState(data.property.id);
      setHasAddedMasterParcelChecklist(false);
      setIsEditingProperty(false);
    } catch (err) {
      console.error(err);
      setError('Could not load selling property details.');
      setChecklistTypeKey('');
      setChecklistTemplateItems([]);
      setChecklistValues({});
      setHasExistingMasterParcelChecklist(false);
      setHasAddedMasterParcelChecklist(false);
    } finally {
      setLoading(false);
    }
  }, [id, loadChecklistData, loadMasterParcelChecklistState]);

  const handleAddMasterParcelChecklist = useCallback(() => {
    setHasAddedMasterParcelChecklist(true);
    setChecklistExpanded(false);
  }, []);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  useEffect(() => {
    if (loading) return;

    const action = searchParams.get('action');
    if (!action) return;

    if (action === 'showing') {
      setShowAddShowingModal(true);
    }

    if (action === 'contact') {
      setShowAddContactModal(true);
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('action');
    setSearchParams(nextParams, { replace: true });
  }, [loading, searchParams, setSearchParams]);

  const sortedShowings = useMemo(
    () => [...showings].sort((left, right) => new Date(right.showingDateTime || 0) - new Date(left.showingDateTime || 0)),
    [showings]
  );

  const editableOwnerDrafts = useMemo(() => {
    if (propertyOwners.length > 0) {
      return propertyOwners.map((owner) => createOwnerDraft(owner.name || ''));
    }

    return ownerTextToDrafts(propertyForm?.ownersText || '');
  }, [propertyOwners, propertyForm?.ownersText]);

  const sortedContacts = useMemo(
    () => [...propertyContacts].sort((left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0)),
    [propertyContacts]
  );

  const handleOpenPropertyContact = useCallback((contactMatch) => {
    if (!contactMatch) {
      return;
    }

    if (contactMatch.contactId) {
      navigate(`/real-estate/contacts?contactId=${contactMatch.contactId}`);
      return;
    }

    const target = document.getElementById(`property-contact-${contactMatch.id}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [navigate]);

  const sortedActivity = useMemo(
    () => [...activity].sort((left, right) => new Date(right.createdAt || 0) - new Date(left.createdAt || 0)),
    [activity]
  );

  const renderDetailValue = (value, formatter = null) => {
    const formattedValue = formatter ? formatter(value) : String(value ?? '').trim();
    const hasValue = formattedValue !== null && formattedValue !== undefined && String(formattedValue).trim() !== '';
    return (
      <div style={hasValue ? detailValueStyle : emptyDetailValueStyle}>
        {hasValue ? formattedValue : 'Not set'}
      </div>
    );
  };

  const renderCommissionValue = (amountValue, amountType, derivedValue) => {
    const amountText = String(amountValue ?? '').trim();
    const derivedText = String(derivedValue ?? '').trim();

    if (!amountText && !derivedText) {
      return renderDetailValue('');
    }

    return (
      <div>
        <div style={detailValueStyle}>
          {amountText ? `${amountText}${amountType === '%' ? '%' : ' €'}` : 'Not set'}
        </div>
        {derivedText ? (
          <div style={{ marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>{derivedText}</div>
        ) : null}
      </div>
    );
  };

  const handleSaveProperty = async () => {
    if (!propertyForm.katastrskaObcina || !propertyForm.idParceleStavbe || !propertyForm.type) {
      window.alert('Please fill in required fields.');
      return;
    }

    setSavingProperty(true);
    setError('');

    try {
      const grossInputValue = propertyForm.grossCommissionType === '%' ? propertyForm.grossCommissionInput : propertyForm.grossCommission;
      const netInputValue = propertyForm.netCommissionType === '%' ? propertyForm.netCommissionInput : propertyForm.netCommission;
      const parsedCoordinates = parseSellingPropertyCoordinates(propertyForm.coordinates);
      if (propertyForm.coordinates && !parsedCoordinates) {
        window.alert('Coordinates must be in format latitude, longitude.');
        setSavingProperty(false);
        return;
      }

      const payload = {
        owners: editableOwnerDrafts.map((owner) => ({ name: String(owner?.name || '').trim() })).filter((owner) => owner.name),
        katastrskaObcina: propertyForm.katastrskaObcina,
        idParceleStavbe: propertyForm.idParceleStavbe,
        type: propertyForm.type,
        ownersText: ownerDraftsToText(editableOwnerDrafts),
        buyersText: propertyForm.buyersText,
        source: propertyForm.source,
        location: propertyForm.location,
        coordinates: parsedCoordinates?.coordinates || propertyForm.coordinates || '',
        inCooperationWith: propertyForm.inCooperationWith,
        propertyType: propertyForm.propertyType,
        surfaceArea: propertyForm.surfaceArea,
        advertisingEnabled: propertyForm.advertisingEnabled,
        adLink: propertyForm.adLink,
        price: propertyForm.price,
        grossCommission: computeCommissionValue({
          value: grossInputValue,
          type: propertyForm.grossCommissionType,
          base: propertyForm.price
        }) ?? '',
        netCommission: computeCommissionValue({
          value: netInputValue,
          type: propertyForm.netCommissionType,
          base: computeCommissionValue({
            value: grossInputValue,
            type: propertyForm.grossCommissionType,
            base: propertyForm.price
          })
        }) ?? '',
        grossCommissionInput: grossInputValue ?? '',
        grossCommissionInputType: propertyForm.grossCommissionType,
        netCommissionInput: netInputValue ?? '',
        netCommissionInputType: propertyForm.netCommissionType,
        createdAt: propertyForm.createdAt || null
      };

      const response = await sendWithContactResolution(
        (nextPayload) => authFetch(`${API_BASE}/api/leads/selling/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextPayload)
        }),
        payload
      );

      if (!response.ok) {
        const message = await getResponseErrorMessage(response, 'Failed to save property');
        throw new Error(message);
      }

      await fetchDetail();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not save selling property.');
    } finally {
      setSavingProperty(false);
    }
  };

  const handleCancelPropertyEditing = () => {
    setError('');
    setIsEditingProperty(false);
    fetchDetail();
  };

  const handleAddShowing = async () => {
    if (!showingForm.showingDateTime || !showingForm.name.trim()) {
      window.alert('Please enter date/time and name.');
      return;
    }

    setSavingShowing(true);

    try {
      const response = await sendWithContactResolution(
        (nextPayload) => authFetch(`${API_BASE}/api/leads/selling/${id}/showings`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextPayload)
        }),
        {
          showingDateTime: showingForm.showingDateTime,
          name: showingForm.name,
          email: showingForm.email,
          phone: showingForm.phone,
          notes: showingForm.notes
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add showing');
      }

      setShowAddShowingModal(false);
      setShowingForm(createEmptyShowingForm());
      await fetchDetail();
    } catch (err) {
      console.error(err);
      window.alert('Could not add showing.');
    } finally {
      setSavingShowing(false);
    }
  };

  const handleAddPropertyContact = async () => {
    if (!contactForm.name.trim()) {
      window.alert('Please enter a name.');
      return;
    }

    setSavingContact(true);

    try {
      const response = await sendWithContactResolution(
        (nextPayload) => authFetch(`${API_BASE}/api/leads/selling/${id}/contacts`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(nextPayload)
        }),
        {
          name: contactForm.name,
          email: contactForm.email,
          phone: contactForm.phone,
          notes: contactForm.notes
        }
      );

      if (!response.ok) {
        throw new Error('Failed to add property contact');
      }

      setShowAddContactModal(false);
      setContactForm(createEmptyContactForm());
      await fetchDetail();
    } catch (err) {
      console.error(err);
      window.alert('Could not add property contact.');
    } finally {
      setSavingContact(false);
    }
  };

  const handleMarkSold = async () => {
    setSavingStatus(true);

    try {
      const grossInputValue = markSoldForm.grossCommissionType === '%' ? markSoldForm.grossCommissionInput : markSoldForm.grossCommission;
      const netInputValue = markSoldForm.netCommissionType === '%' ? markSoldForm.netCommissionInput : markSoldForm.netCommission;

      const response = await authFetch(`${API_BASE}/api/leads/selling/${id}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          status: 'sold',
          price: markSoldForm.price,
          grossCommission: computeCommissionValue({
            value: grossInputValue,
            type: markSoldForm.grossCommissionType,
            base: markSoldForm.price
          }) ?? '',
          netCommission: computeCommissionValue({
            value: netInputValue,
            type: markSoldForm.netCommissionType,
            base: computeCommissionValue({
              value: grossInputValue,
              type: markSoldForm.grossCommissionType,
              base: markSoldForm.price
            })
          }) ?? '',
          buyersText: markSoldForm.buyersText,
          grossCommissionInput: grossInputValue ?? '',
          grossCommissionInputType: markSoldForm.grossCommissionType,
          netCommissionInput: netInputValue ?? '',
          netCommissionInputType: markSoldForm.netCommissionType
        })
      });

      if (!response.ok) {
        throw new Error('Failed to mark property as sold');
      }

      setShowMarkSoldModal(false);
      await fetchDetail();
    } catch (err) {
      console.error(err);
      window.alert('Could not mark property as sold.');
    } finally {
      setSavingStatus(false);
    }
  };

  const handleAddPropertyNote = async () => {
    const note = String(propertyNoteForm.note || '').trim();
    if (!note) {
      window.alert('Please enter a note.');
      return;
    }

    setSavingProperty(true);
    setError('');

    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/${id}/notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note })
      });

      if (!response.ok) {
        const message = await getResponseErrorMessage(response, 'Failed to add property note');
        throw new Error(message);
      }

      setPropertyNoteForm(createEmptyPropertyNoteForm());
      await fetchDetail();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not add property note.');
    } finally {
      setSavingProperty(false);
    }
  };

  const handleDeleteProperty = async () => {
    if (!window.confirm('Delete this selling property?')) {
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/${id}`, { method: 'DELETE' });
      if (!response.ok) {
        throw new Error('Failed to delete selling property');
      }

      navigate('/');
    } catch (err) {
      console.error(err);
      window.alert('Could not delete selling property.');
    }
  };

  const handlePresentationUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!/\.pdf$/i.test(file.name)) {
      window.alert('Please upload a PDF presentation.');
      event.target.value = '';
      return;
    }

    setSavingProperty(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('document', file);
      formData.append('kind', 'presentation');

      const response = await authFetch(`${API_BASE}/api/leads/selling/${id}/documents`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to upload presentation');
      }

      await fetchDetail();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not upload presentation.');
    } finally {
      setSavingProperty(false);
      event.target.value = '';
    }
  };

  const handleDeletePresentation = async () => {
    if (!propertyForm?.presentationDocumentPath) {
      return;
    }

    if (!window.confirm('Delete the current PDF presentation?')) {
      return;
    }

    setSavingProperty(true);
    setError('');

    try {
      const response = await authFetch(`${API_BASE}/api/leads/selling/${id}/documents/presentation`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.error || 'Failed to delete presentation');
      }

      await fetchDetail();
    } catch (err) {
      console.error(err);
      setError(err.message || 'Could not delete presentation.');
    } finally {
      setSavingProperty(false);
    }
  };

  const handleChecklistDocumentUpload = async (_item, file) => {
    if (!file) return null;

    const formData = new FormData();
    formData.append('document', file);

    const response = await authFetch(`${API_BASE}/api/checklists/documents`, {
      method: 'POST',
      body: formData
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to upload document');
    }

    const data = await response.json();
    return {
      documentName: data.documentName,
      documentPath: data.documentPath
    };
  };

  const handleDocumentDownload = async (documentPath, documentName) => {
    if (!documentPath) return;

    try {
      const response = await authFetch(`${API_BASE}/api/leads/documents/${documentPath}`);
      if (!response.ok) {
        throw new Error('Failed to download document');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = documentName || documentPath;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(anchor);
    } catch (err) {
      console.error(err);
      window.alert(`Could not download document: ${err.message}`);
    }
  };

  const fetchChecklistDocumentArrayBuffer = useCallback(async (documentValue) => {
    if (documentValue?.kind === 'zk') {
      if (!documentValue?.zkExtractId) {
        throw new Error('Missing ZK extract id');
      }

      const response = await authFetch(`${API_BASE}/api/zk/${documentValue?.history ? 'download-history' : 'download'}/${documentValue.zkExtractId}`);
      if (!response.ok) {
        throw new Error(`Failed to download ${documentValue.documentName || `ZK ${documentValue?.history ? 'history' : 'extract'}`}`);
      }

      return response.arrayBuffer();
    }

    if (!documentValue?.documentPath) {
      throw new Error('Missing document path');
    }

    const response = await authFetch(`${API_BASE}/api/leads/documents/${documentValue.documentPath}`);
    if (!response.ok) {
      throw new Error(`Failed to download ${documentValue.documentName || 'document'}`);
    }

    return response.arrayBuffer();
  }, []);

  const handleDownloadZkFile = async ({ zkExtractId, ko, idParceleStavbe, history = false }) => {
    if (!zkExtractId) {
      window.alert(`No ZK ${history ? 'history' : 'extract'} available for this property.`);
      return;
    }

    try {
      const response = await authFetch(`${API_BASE}/api/zk/${history ? 'download-history' : 'download'}/${zkExtractId}`);
      if (!response.ok) {
        throw new Error(response.status === 404 ? `ZK ${history ? 'history' : 'extract'} file not found` : `Failed to download ZK ${history ? 'history' : 'extract'}`);
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `${history ? 'ZK_History' : 'ZK_Extract'}_${ko}_${idParceleStavbe}.pdf`;
      document.body.appendChild(anchor);
      anchor.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(anchor);
    } catch (err) {
      console.error(err);
      window.alert(`Could not download ZK ${history ? 'history' : 'extract'}: ${err.message}`);
    }
  };

  const handleDownloadSellingZKExtract = async () => {
    await handleDownloadZkFile({
      zkExtractId: propertyForm?.zkExtractId,
      ko: propertyForm?.katastrskaObcina,
      idParceleStavbe: propertyForm?.idParceleStavbe,
      history: false
    });
  };

  const handleDownloadSellingZKHistory = async () => {
    await handleDownloadZkFile({
      zkExtractId: propertyForm?.zkExtractId,
      ko: propertyForm?.katastrskaObcina,
      idParceleStavbe: propertyForm?.idParceleStavbe,
      history: true
    });
  };

  const fetchZkParcelData = useCallback(async (ko, parcela) => {
    const response = await authFetch(`${API_BASE}/api/zk/latest?ko=${encodeURIComponent(ko)}&id=${encodeURIComponent(parcela)}`);

    if (!response.ok) {
      if (response.status === 404) {
        return { zkExtractId: null, ownerNames: '', owners: [] };
      }

      throw new Error('Failed to fetch ZK parcel data');
    }

    const data = await response.json();
    return {
      zkExtractId: data.zkId || null,
      ownerNames: String(data.ownerNames || '').trim(),
      owners: Array.isArray(data.owners) ? data.owners : []
    };
  }, []);

  const renderChecklistCustomField = useMemo(() => createMasterParcelCustomFieldRenderer({
    propertyForm,
    fetchZkParcelData,
    downloadZkExtract: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: false }),
    downloadZkHistory: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: true }),
    formatEuro,
    computePricePerSquareMeter,
    uploadChecklistDocument: (file) => handleChecklistDocumentUpload(null, file),
    downloadChecklistDocument: (value) => handleDocumentDownload(value?.documentPath, value?.documentName),
    documentAccept: DOCUMENT_ACCEPT
  }), [propertyForm, fetchZkParcelData]);

  const renderChecklistItemContent = useCallback((item, value, options = {}) => {
    const exportMode = options.exportMode === true;

    if (item.type === 'checkbox') {
      return <div style={{ color: '#16a34a', fontSize: '18px', fontWeight: '700', lineHeight: 1 }}>✓</div>;
    }

    if (String(item.type || '').startsWith('mpt_')) {
      return renderMasterParcelSummary({
        item,
        value,
        propertyForm,
        price: propertyForm?.price,
        formatEuro,
        downloadZkExtract: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: false }),
        downloadZkHistory: (zkExtractId, ko, idParceleStavbe) => handleDownloadZkFile({ zkExtractId, ko, idParceleStavbe, history: true }),
        downloadChecklistDocument: (documentValue) => handleDocumentDownload(documentValue?.documentPath, documentValue?.documentName),
        propertyContacts: sortedContacts,
        onOpenPropertyContact: handleOpenPropertyContact,
        exportMode
      });
    }

    if (item.type === 'document') {
      const documentValues = getChecklistDocumentValues(item, value);

      if (exportMode) {
        if (documentValues.length === 0) {
          return <div style={{ color: '#475569' }}>Document</div>;
        }

        return (
          <div style={{ display: 'grid', gap: '6px', color: '#475569' }}>
            {documentValues.map((documentValue, index) => (
              <div key={`${item.id || 'document'}-export-${documentValue.documentPath}-${index}`}>{documentValue.documentName}</div>
            ))}
          </div>
        );
      }

      if (documentValues.length === 0) {
        return <div style={{ color: '#94a3b8', fontSize: '13px' }}>No document uploaded.</div>;
      }

      if (isMultiDocumentChecklistItem(item)) {
        return (
          <div style={{ display: 'grid', gap: '8px' }}>
            {documentValues.map((documentValue, index) => (
              <button
                key={`${item.id || 'document'}-${documentValue.documentPath}-${index}`}
                type="button"
                onClick={() => handleDocumentDownload(documentValue.documentPath, documentValue.documentName)}
                style={{ padding: '7px 10px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px', width: 'fit-content' }}
              >
                <Download size={13} /> {documentValue.documentName}
              </button>
            ))}
          </div>
        );
      }

      const [documentValue] = documentValues;

      return (
        <button
          type="button"
          onClick={() => handleDocumentDownload(documentValue.documentPath, documentValue.documentName)}
          style={{ padding: '7px 10px', backgroundColor: '#8b5cf6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
        >
          <Download size={13} /> {documentValue.documentName || 'Download'}
        </button>
      );
    }

    return <div style={{ color: '#1e293b', fontWeight: '500', whiteSpace: 'pre-wrap' }}>{formatChecklistValue(item, value)}</div>;
  }, [propertyForm, sortedContacts, handleOpenPropertyContact]);

  const handleOpenChecklistExportModal = useCallback(() => {
    const defaultSelection = checklistExportOptions.filter((option) => option.enabled).map((option) => option.id);

    if (defaultSelection.length === 0) {
      window.alert('Nothing is available for PDF export yet.');
      return;
    }

    setChecklistExportSelection(defaultSelection);
    setChecklistExportIncludeCoverPage(true);
    setChecklistExportIncludeItemName(false);
    setShowChecklistExportModal(true);
  }, [checklistExportOptions]);

  const handleToggleChecklistExportOption = useCallback((optionId) => {
    setChecklistExportSelection((current) => current.includes(optionId)
      ? current.filter((value) => value !== optionId)
      : [...current, optionId]);
  }, []);

  const handleExportChecklistPdf = async () => {
    const selectedOptions = checklistExportOptions.filter((option) => checklistExportSelection.includes(option.id));
    if (selectedOptions.length === 0) {
      window.alert('Select at least one export option.');
      return;
    }

    setChecklistExporting(true);

    try {
      const mergedPdf = await PDFDocument.create();
      const { regularFont, boldFont } = await loadPdfFonts(mergedPdf);
      const zkValue = checklistValues[MPT_ZK_ITEM_ID];
      const extraParcels = Array.isArray(zkValue?.extraParcels) ? zkValue.extraParcels : [];
      const parcelNumbers = [String(propertyForm?.idParceleStavbe || '').trim(), ...extraParcels.map((parcel) => String(parcel?.parcela || '').trim())].filter(Boolean);
      const parcelArea = String(checklistValues[MPT_AREA_SECTION_ITEM_ID]?.area || '').trim();
      const pricePerSquareMeter = computePricePerSquareMeter({ area: parcelArea, price: propertyForm?.price });

      if (checklistExportIncludeCoverPage) {
        await addPdfCoverPage(mergedPdf, {
          propertyForm,
          parcelNumbers,
          parcelArea,
          pricePerSquareMeter,
          regularFont,
          boldFont
        });
      }
      const overlayFont = checklistExportIncludeItemName ? boldFont : null;
      const exportWarnings = [];
      const includesChecklistSummary = selectedOptions.some((option) => option.id === 'checklist');

      if (includesChecklistSummary) {
        flushSync(() => {
          setChecklistExportSurfaceVisible(true);
        });
        await waitForNextPaint();
      }

      for (const option of selectedOptions) {
        if (option.id === 'checklist') {
          await addChecklistSummaryFromDomToPdf(mergedPdf, checklistExportRef.current, {
            propertyForm,
            regularFont,
            boldFont,
            overlayEnabled: checklistExportIncludeItemName,
            overlayLabel: option.label,
            overlayFont
          });
          continue;
        }

        for (const documentValue of option.pdfDocuments) {
          try {
            if (mergedPdf.getPageCount() % 2 === 1) {
              mergedPdf.addPage(PageSizes.A4);
            }

            const pdfBytes = await fetchChecklistDocumentArrayBuffer(documentValue);
            await appendPdfToDocument(mergedPdf, pdfBytes, {
              overlayEnabled: checklistExportIncludeItemName,
              overlayLabel: option.label,
              overlayFont
            });
          } catch (error) {
            console.error('Could not include checklist export document:', error);
            exportWarnings.push(`${option.label}: ${documentValue?.documentName || 'document'}`);
          }
        }
      }

      if (mergedPdf.getPageCount() === 0) {
        throw new Error('No PDF content was available for the selected export items.');
      }

      const mergedPdfBytes = await mergedPdf.save();
      downloadArrayBufferAsFile(
        mergedPdfBytes,
        `selling_checklist_${propertyForm?.katastrskaObcina || 'property'}_${propertyForm?.idParceleStavbe || 'export'}_${new Date().toISOString().split('T')[0]}.pdf`
      );

      setShowChecklistExportModal(false);

      if (exportWarnings.length > 0) {
        window.alert(`Export completed, but some PDFs could not be included:\n${exportWarnings.join('\n')}`);
      }
    } catch (error) {
      console.error(error);
      window.alert(`Could not export checklist PDF: ${error.message}`);
    } finally {
      flushSync(() => {
        setChecklistExportSurfaceVisible(false);
      });
      setChecklistExporting(false);
    }
  };

  const handleSaveChecklistValues = async (nextValues) => {
    if (!propertyForm?.id || !checklistTypeKey) {
      return;
    }

    setChecklistSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/checklists/entry`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: SELLING_CHECKLIST_SCOPE,
          typeKey: checklistTypeKey,
          entityType: SELLING_CHECKLIST_ENTITY_TYPE,
          entityId: propertyForm.id,
          values: nextValues,
          userName: localStorage.getItem('leadGenUser') || 'User'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save checklist');
      }

      const data = await response.json();
      setChecklistValues(data.values && typeof data.values === 'object' ? data.values : {});
      setChecklistExpanded(true);
      setShowChecklistModal(false);
      await fetchDetail();
    } catch (err) {
      console.error(err);
      window.alert(`Could not save checklist: ${err.message}`);
    } finally {
      setChecklistSaving(false);
    }
  };

  const handleSaveChecklistTemplate = async (items) => {
    if (!checklistTypeKey) {
      return;
    }

    setChecklistSaving(true);
    try {
      const response = await authFetch(`${API_BASE}/api/checklists/template`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope: SELLING_CHECKLIST_SCOPE,
          typeKey: checklistTypeKey,
          items
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save checklist template');
      }

      const data = await response.json();
      setChecklistTemplateItems(normalizeChecklistTemplateItems(data.items));
      await loadChecklistData(propertyForm?.id, checklistTypeKey);
      setChecklistExpanded(true);
    } catch (err) {
      console.error(err);
      window.alert(`Could not save checklist template: ${err.message}`);
    } finally {
      setChecklistSaving(false);
    }
  };

  if (loading && !propertyForm) {
    return <div style={{ padding: '24px', color: '#64748b' }}>Loading property...</div>;
  }

  if (!propertyForm) {
    return <div style={{ padding: '24px', color: '#dc2626' }}>{error || 'Property not found.'}</div>;
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', paddingBottom: '40px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '24px' }}>
        <div>
          <button
            onClick={() => navigate('/')}
            style={{
              marginBottom: '10px',
              padding: '6px 10px',
              borderRadius: '999px',
              border: '1px solid #cbd5e1',
              backgroundColor: '#f8fafc',
              color: '#0f172a',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: '600'
            }}
          >
            Back to Dashboard
          </button>
          <h1 style={{ margin: 0, color: '#0f172a', fontSize: '30px' }}>
            {propertyForm.katastrskaObcina} {propertyForm.idParceleStavbe}
          </h1>
          <div style={{ marginTop: '6px', color: '#64748b', fontSize: '14px' }}>
            {propertyForm.type} · Status: {propertyForm.status || 'selling'}
            {propertyForm.soldAt ? ` · Sold at ${formatDateTime(propertyForm.soldAt)}` : ''}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
          <button
            onClick={() => setIsEditingProperty((current) => !current)}
            style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: isEditingProperty ? '#eff6ff' : '#f8fafc', color: '#0f172a', cursor: 'pointer', fontWeight: '600' }}
          >
            {isEditingProperty ? 'Editing' : 'Edit'}
          </button>
          <button
            onClick={() => setShowAddShowingModal(true)}
            style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#0ea5e9', color: 'white', cursor: 'pointer', fontWeight: '600' }}
          >
            Ogled
          </button>
          <button
            onClick={() => setShowAddContactModal(true)}
            style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#10b981', color: 'white', cursor: 'pointer', fontWeight: '600' }}
          >
            Kontakt
          </button>
          {isEditingProperty ? (
            <>
              <button
                onClick={handleSaveProperty}
                disabled={savingProperty}
                style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#2563eb', color: 'white', cursor: 'pointer', fontWeight: '600' }}
              >
                {savingProperty ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancelPropertyEditing}
                disabled={savingProperty}
                style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: 'pointer', fontWeight: '600' }}
              >
                Cancel edit
              </button>
            </>
          ) : null}
          <button
            onClick={() => setShowMarkSoldModal(true)}
            disabled={savingStatus}
            style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#16a34a', color: 'white', cursor: 'pointer', fontWeight: '600' }}
          >
            Mark as sold
          </button>
          <button
            onClick={handleDeleteProperty}
            style={{ padding: '10px 14px', borderRadius: '10px', border: '1px solid #fecaca', backgroundColor: '#fff1f2', color: '#b91c1c', cursor: 'pointer', fontWeight: '600' }}
          >
            Delete
          </button>
        </div>
      </div>

      {error ? <div style={{ marginBottom: '16px', color: '#dc2626' }}>{error}</div> : null}

      <div style={{ ...sectionCardStyle, marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
          <h2 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Property details</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center', flexWrap: 'wrap' }}>
            {propertyForm.presentationDocumentPath ? (
              <button
                type="button"
                onClick={() => handleDocumentDownload(propertyForm.presentationDocumentPath, propertyForm.presentationDocumentName || 'presentation.pdf')}
                style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#eff6ff', color: '#2563eb', cursor: 'pointer', fontWeight: '600' }}
              >
                Download presentation
              </button>
            ) : null}
            {propertyForm.presentationDocumentPath ? (
              <button
                type="button"
                onClick={handleDeletePresentation}
                disabled={savingProperty}
                style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #fecaca', backgroundColor: '#fff1f2', color: '#b91c1c', cursor: savingProperty ? 'not-allowed' : 'pointer', fontWeight: '600' }}
              >
                Delete presentation
              </button>
            ) : null}
            <label style={{ padding: '8px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: 'pointer', fontWeight: '600' }}>
              <input type="file" accept=".pdf" onChange={handlePresentationUpload} style={{ display: 'none' }} />
              {propertyForm.presentationDocumentPath ? 'Replace presentation' : 'Upload presentation'}
            </label>
          </div>
        </div>
        <div style={{ display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))' }}>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Podpis posr. pogodbe</label>
            {isEditingProperty ? (
              <input
                type="datetime-local"
                value={toDateTimeLocalValue(propertyForm.createdAt)}
                onChange={(event) => setPropertyForm((current) => ({ ...current, createdAt: event.target.value ? new Date(event.target.value).toISOString() : null }))}
                disabled={!isEditingProperty}
                style={getDetailFieldStyle(isEditingProperty)}
              />
            ) : renderDetailValue(propertyForm.createdAt, (value) => (value ? formatDateTime(value) : ''))}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Katastrska občina</label>
            {isEditingProperty ? (
              <input
                value={propertyForm.katastrskaObcina}
                onChange={(event) => setPropertyForm((current) => ({ ...current, katastrskaObcina: event.target.value }))}
                readOnly={!isEditingProperty}
                style={getDetailFieldStyle(isEditingProperty)}
              />
            ) : renderDetailValue(propertyForm.katastrskaObcina)}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>ID parcele / stavbe</label>
            {isEditingProperty ? (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <input
                  value={propertyForm.idParceleStavbe}
                  onChange={(event) => setPropertyForm((current) => ({ ...current, idParceleStavbe: event.target.value }))}
                  readOnly={!isEditingProperty}
                  style={{ ...getDetailFieldStyle(isEditingProperty), flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => copyToClipboard(`${propertyForm.katastrskaObcina || ''} ${propertyForm.idParceleStavbe || ''}`.trim(), 'Failed to copy selling property KO ID:')}
                  disabled={!String(propertyForm.idParceleStavbe || '').trim()}
                  style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: String(propertyForm.idParceleStavbe || '').trim() ? 'pointer' : 'not-allowed', fontWeight: '600' }}
                >
                  Copy ID
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <div style={{ flex: '1 1 auto', minWidth: 0 }}>
                  {renderDetailValue(propertyForm.idParceleStavbe)}
                </div>
                <button
                  type="button"
                  onClick={() => copyToClipboard(`${propertyForm.katastrskaObcina || ''} ${propertyForm.idParceleStavbe || ''}`.trim(), 'Failed to copy selling property KO ID:')}
                  disabled={!String(propertyForm.idParceleStavbe || '').trim()}
                  style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: String(propertyForm.idParceleStavbe || '').trim() ? 'pointer' : 'not-allowed', fontWeight: '600' }}
                >
                  Copy ID
                </button>
              </div>
            )}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Tip</label>
            {isEditingProperty ? (
              <select
                value={propertyForm.type}
                onChange={(event) => setPropertyForm((current) => ({ ...current, type: normalizeSellingPropertyType(event.target.value) }))}
                style={getDetailFieldStyle(isEditingProperty)}
              >
                <option value="">Select type</option>
                {SELLING_PROPERTY_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            ) : renderDetailValue(propertyForm.type)}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', marginBottom: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <input
                type="checkbox"
                checked={propertyForm.advertisingEnabled !== false}
                onChange={(event) => setPropertyForm((current) => ({ ...current, advertisingEnabled: event.target.checked }))}
                disabled={!isEditingProperty}
                style={{ cursor: isEditingProperty ? 'pointer' : 'default' }}
              />
              <span>Oglašujemo</span>
            </label>
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Tip nepremičnine</label>
            {isEditingProperty ? (
              <select
                value={propertyForm.propertyType || ''}
                onChange={(event) => setPropertyForm((current) => ({ ...current, propertyType: event.target.value }))}
                style={getDetailFieldStyle(isEditingProperty)}
              >
                <option value="">Select property type</option>
                {SELLING_PROPERTY_REAL_ESTATE_TYPE_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            ) : renderDetailValue(propertyForm.propertyType)}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Površina (m²)</label>
            {isEditingProperty ? (
              <input
                type="number"
                value={propertyForm.surfaceArea || ''}
                onChange={(event) => setPropertyForm((current) => ({ ...current, surfaceArea: event.target.value }))}
                style={getDetailFieldStyle(isEditingProperty)}
              />
            ) : renderDetailValue(propertyForm.surfaceArea, (value) => (String(value ?? '').trim() ? `${value} m²` : ''))}
          </div>
          {propertyForm.advertisingEnabled ? (
            <div>
              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Link do oglasa</label>
              {isEditingProperty ? (
                <input
                  type="url"
                  value={propertyForm.adLink || ''}
                  onChange={(event) => setPropertyForm((current) => ({ ...current, adLink: event.target.value }))}
                  style={getDetailFieldStyle(isEditingProperty)}
                />
              ) : renderDetailValue(propertyForm.adLink, (value) => (
                String(value ?? '').trim()
                  ? <a href={value} target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc', textDecoration: 'underline' }}>Oglas</a>
                  : ''
              ))}
            </div>
          ) : null}
          {normalizeSellingPropertyType(propertyForm.type) === 'cooperation' ? (
            <div>
              <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>In cooperation with</label>
              {isEditingProperty ? (
                <input
                  value={propertyForm.inCooperationWith || ''}
                  onChange={(event) => setPropertyForm((current) => ({ ...current, inCooperationWith: event.target.value }))}
                  style={getDetailFieldStyle(isEditingProperty)}
                />
              ) : renderDetailValue(propertyForm.inCooperationWith)}
            </div>
          ) : null}
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Vir</label>
            {isEditingProperty ? (
              <input
                value={propertyForm.source}
                onChange={(event) => setPropertyForm((current) => ({ ...current, source: event.target.value }))}
                readOnly={!isEditingProperty}
                style={getDetailFieldStyle(isEditingProperty)}
              />
            ) : renderDetailValue(propertyForm.source)}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Coordinates</label>
            {isEditingProperty ? (
              <input
                value={propertyForm.coordinates || ''}
                onChange={(event) => setPropertyForm((current) => ({ ...current, coordinates: event.target.value }))}
                readOnly={!isEditingProperty}
                placeholder="46.042588, 14.648992"
                style={getDetailFieldStyle(isEditingProperty)}
              />
            ) : renderDetailValue(propertyForm.coordinates)}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Lokacija</label>
            {isEditingProperty ? (
              <input
                value={propertyForm.location || ''}
                onChange={(event) => setPropertyForm((current) => ({ ...current, location: event.target.value }))}
                readOnly={!isEditingProperty}
                style={getDetailFieldStyle(isEditingProperty)}
              />
            ) : (
              <button
                type="button"
                onClick={() => copyToClipboard(propertyForm.location, 'Failed to copy selling property location:')}
                disabled={!String(propertyForm.location || '').trim()}
                title={String(propertyForm.location || '').trim() ? 'Click to copy' : ''}
                style={{
                  width: '100%',
                  padding: 0,
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: String(propertyForm.location || '').trim() ? 'copy' : 'default'
                }}
              >
                {renderDetailValue(propertyForm.location)}
              </button>
            )}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Cena (€)</label>
            {isEditingProperty ? (
              <input
                value={propertyForm.price}
                onChange={(event) => setPropertyForm((current) => ({ ...current, price: event.target.value }))}
                readOnly={!isEditingProperty}
                style={getDetailFieldStyle(isEditingProperty)}
              />
            ) : renderDetailValue(propertyForm.price, (value) => (String(value ?? '').trim() ? formatEuro(value) : ''))}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Bruto provizija</label>
            {isEditingProperty ? (
              <>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <input
                    value={propertyForm.grossCommissionType === '%' ? propertyForm.grossCommissionInput : propertyForm.grossCommission}
                    onChange={(event) => setPropertyForm((current) => (
                      current.grossCommissionType === '%'
                        ? { ...current, grossCommissionInput: event.target.value }
                        : { ...current, grossCommission: event.target.value }
                    ))}
                    readOnly={!isEditingProperty}
                    style={{ ...getDetailFieldStyle(isEditingProperty), flex: '1 1 180px' }}
                  />
                  <select
                    value={propertyForm.grossCommissionType}
                    onChange={(event) => setPropertyForm((current) => ({ ...current, grossCommissionType: event.target.value }))}
                    disabled={!isEditingProperty}
                    style={{ ...getDetailFieldStyle(isEditingProperty), width: '74px', flex: '0 0 74px' }}
                  >
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>
                  {propertyForm.grossCommissionType === '%'
                    ? formatEuro(computeCommissionValue({ value: propertyForm.grossCommissionInput, type: '%', base: propertyForm.price }))
                    : formatPercent(computePercentValue({ value: propertyForm.grossCommission, base: propertyForm.price }))}
                </div>
              </>
            ) : renderCommissionValue(
              propertyForm.grossCommissionType === '%' ? propertyForm.grossCommissionInput : propertyForm.grossCommission,
              propertyForm.grossCommissionType,
              propertyForm.grossCommissionType === '%'
                ? formatEuro(computeCommissionValue({ value: propertyForm.grossCommissionInput, type: '%', base: propertyForm.price }))
                : formatPercent(computePercentValue({ value: propertyForm.grossCommission, base: propertyForm.price }))
            )}
          </div>
          <div>
            <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Neto provizija</label>
            {isEditingProperty ? (
              <>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <input
                    value={propertyForm.netCommissionType === '%' ? propertyForm.netCommissionInput : propertyForm.netCommission}
                    onChange={(event) => setPropertyForm((current) => (
                      current.netCommissionType === '%'
                        ? { ...current, netCommissionInput: event.target.value }
                        : { ...current, netCommission: event.target.value }
                    ))}
                    readOnly={!isEditingProperty}
                    style={{ ...getDetailFieldStyle(isEditingProperty), flex: '1 1 180px' }}
                  />
                  <select
                    value={propertyForm.netCommissionType}
                    onChange={(event) => setPropertyForm((current) => ({ ...current, netCommissionType: event.target.value }))}
                    disabled={!isEditingProperty}
                    style={{ ...getDetailFieldStyle(isEditingProperty), width: '74px', flex: '0 0 74px' }}
                  >
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#94a3b8' }}>
                  {propertyForm.netCommissionType === '%'
                    ? formatEuro(computeCommissionValue({
                        value: propertyForm.netCommissionInput,
                        type: '%',
                        base: computeCommissionValue({
                          value: propertyForm.grossCommissionType === '%' ? propertyForm.grossCommissionInput : propertyForm.grossCommission,
                          type: propertyForm.grossCommissionType,
                          base: propertyForm.price
                        })
                      }))
                    : formatPercent(computePercentValue({
                        value: propertyForm.netCommission,
                        base: computeCommissionValue({
                          value: propertyForm.grossCommissionType === '%' ? propertyForm.grossCommissionInput : propertyForm.grossCommission,
                          type: propertyForm.grossCommissionType,
                          base: propertyForm.price
                        })
                      }))}
                </div>
              </>
            ) : renderCommissionValue(
              propertyForm.netCommissionType === '%' ? propertyForm.netCommissionInput : propertyForm.netCommission,
              propertyForm.netCommissionType,
              propertyForm.netCommissionType === '%'
                ? formatEuro(computeCommissionValue({
                    value: propertyForm.netCommissionInput,
                    type: '%',
                    base: computeCommissionValue({
                      value: propertyForm.grossCommissionType === '%' ? propertyForm.grossCommissionInput : propertyForm.grossCommission,
                      type: propertyForm.grossCommissionType,
                      base: propertyForm.price
                    })
                  }))
                : formatPercent(computePercentValue({
                    value: propertyForm.netCommission,
                    base: computeCommissionValue({
                      value: propertyForm.grossCommissionType === '%' ? propertyForm.grossCommissionInput : propertyForm.grossCommission,
                      type: propertyForm.grossCommissionType,
                      base: propertyForm.price
                    })
                  }))
            )}
          </div>
        </div>

        <div style={{ marginTop: '14px' }}>
          <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Owners</label>
          {isEditingProperty ? (
            <div style={{ display: 'grid', gap: '8px' }}>
              {editableOwnerDrafts.map((owner, ownerIndex) => (
                <div key={`property-owner-${ownerIndex}`} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input
                    value={owner.name}
                    onChange={(event) => {
                      const nextOwners = [...editableOwnerDrafts];
                      nextOwners[ownerIndex] = { ...nextOwners[ownerIndex], name: event.target.value };
                      setPropertyForm((current) => ({ ...current, ownersText: ownerDraftsToText(nextOwners) }));
                      setPropertyOwners(nextOwners.map((item, index) => ({ ...(propertyOwners[index] || {}), name: item.name })));
                    }}
                    readOnly={!isEditingProperty}
                    style={{ ...getDetailFieldStyle(isEditingProperty), flex: 1 }}
                    placeholder={`Owner ${ownerIndex + 1}`}
                  />
                  {editableOwnerDrafts.length > 1 ? (
                    <button
                      type="button"
                      onClick={() => {
                        const nextOwners = editableOwnerDrafts.filter((_, index) => index !== ownerIndex);
                        setPropertyForm((current) => ({ ...current, ownersText: ownerDraftsToText(nextOwners) }));
                        setPropertyOwners((current) => {
                          const next = current.filter((_, index) => index !== ownerIndex);
                          return next.length > 0 ? next : [createOwnerDraft()];
                        });
                      }}
                      style={{ padding: '10px 12px', borderRadius: '10px', border: '1px solid #fecaca', backgroundColor: '#fff1f2', color: '#b91c1c', cursor: 'pointer', fontWeight: '600' }}
                    >
                      Remove
                    </button>
                  ) : null}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '6px' }}>
              {editableOwnerDrafts.length > 0 ? editableOwnerDrafts.map((owner, ownerIndex) => (
                <div key={`property-owner-value-${ownerIndex}`} style={owner.name ? detailValueStyle : emptyDetailValueStyle}>
                  {owner.name || 'Not set'}
                </div>
              )) : (
                <div style={emptyDetailValueStyle}>Not set</div>
              )}
            </div>
          )}
          {isEditingProperty ? (
            <button
              type="button"
              onClick={() => setPropertyOwners((current) => [...current, createOwnerDraft()])}
              style={{ marginTop: '8px', padding: '8px 12px', borderRadius: '999px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: 'pointer', fontSize: '12px', fontWeight: '600' }}
            >
              Add another owner
            </button>
          ) : null}
        </div>

        <div style={{ marginTop: '14px', display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <div style={{ color: '#64748b', fontSize: '13px' }}>Created: {formatDateTime(propertyForm.createdAt)}</div>
          {propertyForm.leadId ? (
            <button
              onClick={() => navigate(`/real-estate/lead-gen?leadId=${propertyForm.leadId}`)}
              style={{ ...cardLinkButtonStyle, backgroundColor: '#dbeafe', color: '#1d4ed8', borderColor: '#bfdbfe' }}
            >
              Open in Lead Gen
            </button>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'grid', gap: '24px', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', alignItems: 'start' }}>
        <div style={sectionCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '10px' }}>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Owners</h2>
          </div>

          {propertyOwners.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: '14px' }}>No owners linked yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              {propertyOwners.map((owner, index) => (
                <div key={owner.id || `owner-${index}`} style={{ border: '1px solid #fed7aa', backgroundColor: '#fff7ed', borderRadius: '14px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '700', color: '#9a3412' }}>{owner.name || 'Unnamed owner'}</div>
                    <div style={{ fontSize: '12px', color: '#c2410c' }}>{formatDateTime(owner.createdAt)}</div>
                  </div>
                  {owner.contactId ? (
                    <button
                      onClick={() => navigate(`/real-estate/contacts?contactId=${owner.contactId}`)}
                      style={{ ...cardLinkButtonStyle, backgroundColor: '#ffedd5', color: '#9a3412', borderColor: '#fdba74' }}
                    >
                      Open contact{owner.contactName ? `: ${owner.contactName}` : ''}
                    </button>
                  ) : (
                    <div style={{ fontSize: '12px', color: '#c2410c' }}>No linked contact yet.</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={sectionCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '10px' }}>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Ogledi</h2>
            <button
              onClick={() => setShowAddShowingModal(true)}
              style={{ padding: '8px 12px', borderRadius: '999px', border: 'none', backgroundColor: '#0ea5e9', color: 'white', cursor: 'pointer', fontWeight: '600' }}
            >
              Add ogled
            </button>
          </div>

          {sortedShowings.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: '14px' }}>No ogledi yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              {sortedShowings.map((showing) => (
                <div key={showing.id} style={{ border: '1px solid #bae6fd', backgroundColor: '#f0f9ff', borderRadius: '14px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '700', color: '#0c4a6e' }}>{showing.name || 'Unnamed showing'}</div>
                    <div style={{ fontSize: '12px', color: '#0369a1' }}>{formatDateTime(showing.showingDateTime)}</div>
                  </div>
                  {showing.contactId ? (
                    <div style={{ marginBottom: '8px' }}>
                      <button
                        onClick={() => navigate(`/real-estate/contacts?contactId=${showing.contactId}`)}
                        style={cardLinkButtonStyle}
                      >
                        Open contact{showing.contactName ? `: ${showing.contactName}` : ''}
                      </button>
                    </div>
                  ) : null}
                  {showing.email ? <div style={{ fontSize: '13px', color: '#0f172a' }}>Email: {showing.email}</div> : null}
                  {showing.phone ? <div style={{ fontSize: '13px', color: '#0f172a' }}>Phone: {showing.phone}</div> : null}
                  {showing.notes ? <div style={{ fontSize: '13px', color: '#0f172a', marginTop: '6px', whiteSpace: 'pre-wrap' }}>Notes: {showing.notes}</div> : null}
                  <div style={{ marginTop: '8px', fontSize: '12px', color: '#475569' }}>Created: {formatDateTime(showing.createdAt)}</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={sectionCardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '10px' }}>
            <h2 style={{ margin: 0, color: '#0f172a', fontSize: '20px' }}>Kontakti</h2>
            <button
              onClick={() => setShowAddContactModal(true)}
              style={{ padding: '8px 12px', borderRadius: '999px', border: 'none', backgroundColor: '#10b981', color: 'white', cursor: 'pointer', fontWeight: '600' }}
            >
              Add kontakt
            </button>
          </div>

          {sortedContacts.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: '14px' }}>No property contacts yet.</div>
          ) : (
            <div style={{ display: 'grid', gap: '14px' }}>
              {sortedContacts.map((contact) => (
                <div id={`property-contact-${contact.id}`} key={contact.id} style={{ border: '1px solid #a7f3d0', backgroundColor: '#ecfdf5', borderRadius: '14px', padding: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap', marginBottom: '8px' }}>
                    <div style={{ fontWeight: '700', color: '#065f46' }}>{contact.name || 'Unnamed contact'}</div>
                    <div style={{ fontSize: '12px', color: '#047857' }}>{formatDateTime(contact.createdAt)}</div>
                  </div>
                  {contact.contactId ? (
                    <div style={{ marginBottom: '8px' }}>
                      <button
                        onClick={() => navigate(`/real-estate/contacts?contactId=${contact.contactId}`)}
                        style={{ ...cardLinkButtonStyle, backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' }}
                      >
                        Open contact{contact.contactName ? `: ${contact.contactName}` : ''}
                      </button>
                    </div>
                  ) : null}
                  {contact.address ? <div style={{ fontSize: '13px', color: '#0f172a' }}>Address: {contact.address}</div> : null}
                  {contact.email ? <div style={{ fontSize: '13px', color: '#0f172a' }}>Email: {contact.email}</div> : null}
                  {contact.phone ? <div style={{ fontSize: '13px', color: '#0f172a' }}>Phone: {contact.phone}</div> : null}
                  {contact.notes ? <div style={{ fontSize: '13px', color: '#0f172a', marginTop: '6px', whiteSpace: 'pre-wrap' }}>Notes: {contact.notes}</div> : null}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {hasMasterParcelChecklist ? (
        <div style={{ marginTop: '24px' }}>
          <MasterParcelChecklistSection
            entityId={propertyForm?.id}
            entityType={SELLING_CHECKLIST_ENTITY_TYPE}
            scope="selling_property_mpt"
            propertyForm={propertyForm}
            propertyContacts={sortedContacts}
            onOpenPropertyContact={handleOpenPropertyContact}
            title="MPT Checklist"
            defaultExpanded={true}
          />
        </div>
      ) : null}

      {!hasMasterParcelChecklist ? (
      <div style={{ ...sectionCardStyle, marginTop: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: checklistExpanded ? '16px' : 0, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={() => setChecklistExpanded((current) => !current)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: '10px', padding: 0, border: 'none', background: 'transparent', color: '#0f172a', cursor: 'pointer' }}
          >
            <CheckSquare size={20} color="#d97706" />
            <span style={{ fontSize: '20px', fontWeight: '700' }}>Checklist</span>
            {checklistExpanded ? <ChevronUp size={18} color="#64748b" /> : <ChevronDown size={18} color="#64748b" />}
          </button>
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={handleAddMasterParcelChecklist}
              disabled={!propertyForm?.id}
              style={{ padding: '8px 12px', borderRadius: '999px', border: '1px solid #bbf7d0', backgroundColor: '#ecfdf5', color: '#166534', cursor: propertyForm?.id ? 'pointer' : 'not-allowed', fontWeight: '600' }}
            >
              Add MPT Checklist
            </button>
            <button
              type="button"
              onClick={handleOpenChecklistExportModal}
              disabled={!checklistTypeKey || checklistLoading}
              style={{ padding: '8px 12px', borderRadius: '999px', border: '1px solid #bfdbfe', backgroundColor: '#eff6ff', color: '#1d4ed8', cursor: checklistTypeKey && !checklistLoading ? 'pointer' : 'not-allowed', fontWeight: '600' }}
            >
              Export to PDF
            </button>
            <button
              type="button"
              onClick={() => setShowChecklistModal(true)}
              disabled={!checklistTypeKey}
              style={{ padding: '8px 12px', borderRadius: '999px', border: '1px solid #fdba74', backgroundColor: '#fff7ed', color: '#9a3412', cursor: checklistTypeKey ? 'pointer' : 'not-allowed', fontWeight: '600' }}
            >
              Edit
            </button>
          </div>
        </div>

        {checklistExpanded ? (
          <div style={{ display: 'grid', gap: '18px' }}>
            {checklistLoading ? (
              <div style={{ color: '#94a3b8', fontSize: '14px' }}>Loading checklist...</div>
            ) : filledChecklistItems.length === 0 ? (
              <div style={{ color: '#94a3b8', fontSize: '14px' }}>No checklist items filled yet.</div>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {filledChecklistItems.map(({ item, value }) => (
                  <div key={item.id} style={{ padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{item.label}</div>
                    {renderChecklistItemContent(item, value)}
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : null}
      </div>
      ) : null}

      <div style={{ ...sectionCardStyle, marginTop: '24px' }}>
        <h2 style={{ margin: '0 0 18px', color: '#0f172a', fontSize: '20px' }}>Activity history</h2>
        <div style={{ marginBottom: '18px', display: 'grid', gap: '10px' }}>
          <label style={{ fontSize: '12px', color: '#64748b', display: 'block' }}>Add note</label>
          <textarea
            value={propertyNoteForm.note}
            onChange={(event) => setPropertyNoteForm({ note: event.target.value })}
            placeholder="Write a note for this property..."
            style={{ width: '100%', minHeight: '96px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', resize: 'vertical' }}
          />
          <div>
            <button
              onClick={handleAddPropertyNote}
              disabled={savingProperty}
              style={{ padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#0f172a', color: 'white', cursor: 'pointer', fontWeight: '600' }}
            >
              {savingProperty ? 'Saving...' : 'Save note'}
            </button>
          </div>
        </div>
        {sortedActivity.length === 0 ? (
          <div style={{ color: '#94a3b8', fontSize: '14px' }}>No activity recorded yet.</div>
        ) : (
          <div style={{ display: 'grid', gap: '12px' }}>
            {sortedActivity.map((item) => {
              const isNoteActivity = item.eventType === 'note_added';

              return (
              <div
                key={item.id}
                style={{
                  border: isNoteActivity ? '1px solid #fdba74' : '1px solid #e2e8f0',
                  borderRadius: '14px',
                  padding: '14px',
                  backgroundColor: isNoteActivity ? '#fff7ed' : '#f8fafc'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                  <div style={{ fontWeight: '700', color: isNoteActivity ? '#9a3412' : '#0f172a' }}>{item.description}</div>
                  <div style={{ fontSize: '12px', color: isNoteActivity ? '#c2410c' : '#64748b' }}>{formatDateTime(item.createdAt)}</div>
                </div>
                {item.createdBy ? <div style={{ marginTop: '6px', fontSize: '12px', color: isNoteActivity ? '#c2410c' : '#64748b' }}>By: {item.createdBy}</div> : null}
                {Array.isArray(item.eventPayload?.changes) && item.eventPayload.changes.length > 0 ? (
                  <div style={{ marginTop: '10px', display: 'grid', gap: '8px' }}>
                    {item.eventPayload.changes.map((change, index) => (
                      <div key={`${item.id}-${change.fieldName}-${index}`} style={{ fontSize: '13px', color: '#334155' }}>
                        <strong>{activityFieldLabels[change.fieldName] || change.fieldName}:</strong> {formatActivityValue(change.oldValue)} → {formatActivityValue(change.newValue)}
                      </div>
                    ))}
                  </div>
                ) : null}
                {typeof item.eventPayload?.note === 'string' && item.eventPayload.note.trim() ? (
                  <div style={{ marginTop: '10px', fontSize: '13px', color: isNoteActivity ? '#7c2d12' : '#334155', whiteSpace: 'pre-wrap' }}>
                    {item.eventPayload.note}
              </div>
                ) : null}
              </div>
            );})}
          </div>
        )}
      </div>

      {checklistExportSurfaceVisible ? (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(255,255,255,0.96)', zIndex: 2147483647, overflow: 'auto', padding: '24px' }} aria-hidden="true">
          <div style={{ width: '210mm', margin: '0 auto', padding: 0, backgroundColor: '#ffffff' }}>
            <div ref={checklistExportRef}>
              <div style={{ width: '210mm', boxSizing: 'border-box', padding: '10mm', backgroundColor: '#ffffff', display: 'inline-block' }}>
                <div style={{ marginBottom: '18px' }}>
                  <div style={{ color: '#0f172a', fontSize: '22px', fontWeight: '700', marginBottom: '6px' }}>Splošne informacije</div>
                  <div style={{ color: '#64748b', fontSize: '13px' }}>{propertyForm?.katastrskaObcina} {propertyForm?.idParceleStavbe}</div>
                </div>
                {filledChecklistItems.length === 0 ? (
                  <div style={{ color: '#94a3b8', fontSize: '14px' }}>Splošne informacije še nimajo izpolnjenih postavk.</div>
                ) : (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {filledChecklistItems.map(({ item, value }) => (
                      <div className="checklist-export-item" key={`export-${item.id}`} style={{ padding: '12px 14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '1px solid #e2e8f0', breakInside: 'avoid', pageBreakInside: 'avoid' }}>
                        <div style={{ color: '#64748b', fontSize: '12px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: '6px' }}>{item.label}</div>
                        {renderChecklistItemContent(item, value, { exportMode: true })}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showAddShowingModal ? (
        <div style={modalBackdropStyle}>
          <div style={modalCardStyle}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Add ogled</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Date and time</label>
                <input
                  type="datetime-local"
                  value={showingForm.showingDateTime}
                  onChange={(event) => setShowingForm((current) => ({ ...current, showingDateTime: event.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Name</label>
                <input
                  value={showingForm.name}
                  onChange={(event) => setShowingForm((current) => ({ ...current, name: event.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Email</label>
                <input
                  value={showingForm.email}
                  onChange={(event) => setShowingForm((current) => ({ ...current, email: event.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Phone</label>
                <input
                  value={showingForm.phone}
                  onChange={(event) => setShowingForm((current) => ({ ...current, phone: event.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Notes</label>
                <textarea
                  value={showingForm.notes}
                  onChange={(event) => setShowingForm((current) => ({ ...current, notes: event.target.value }))}
                  style={{ width: '100%', minHeight: '90px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
              <button onClick={handleAddShowing} disabled={savingShowing} style={{ flex: 1, minWidth: '140px', padding: '10px', borderRadius: '10px', border: 'none', backgroundColor: '#0ea5e9', color: 'white', cursor: 'pointer', fontWeight: '600' }}>{savingShowing ? 'Saving...' : 'Save ogled'}</button>
              <button onClick={() => setShowAddShowingModal(false)} style={{ flex: 1, minWidth: '140px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      {showChecklistExportModal ? (
        <div style={modalBackdropStyle}>
          <div style={{ ...modalCardStyle, maxWidth: '640px' }}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Export checklist to PDF</h3>
            <div style={{ color: '#475569', fontSize: '14px', marginBottom: '14px' }}>
              Select the checklist summary and any checklist items with PDF documents you want to merge into one file.
            </div>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', color: '#0f172a', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={checklistExportIncludeCoverPage}
                disabled={checklistExporting}
                onChange={(event) => setChecklistExportIncludeCoverPage(event.target.checked)}
              />
              Include first page
            </label>

            <label style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px', color: '#0f172a', fontWeight: '500' }}>
              <input
                type="checkbox"
                checked={checklistExportIncludeItemName}
                disabled={checklistExporting}
                onChange={(event) => setChecklistExportIncludeItemName(event.target.checked)}
              />
              Overlay item name on each exported page
            </label>

            <div style={{ display: 'grid', gap: '10px', maxHeight: '360px', overflowY: 'auto' }}>
              {checklistExportOptions.map((option) => {
                const countLabel = option.id === 'checklist'
                  ? 'Rendered from the checklist view'
                  : option.enabled
                    ? `${option.pdfDocuments.length} PDF${option.pdfDocuments.length === 1 ? '' : 's'} available`
                    : option.totalDocuments > 0
                      ? 'No PDF documents available'
                      : 'No documents uploaded';

                return (
                  <label
                    key={`export-option-${option.id}`}
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      padding: '12px 14px',
                      borderRadius: '12px',
                      border: '1px solid #e2e8f0',
                      backgroundColor: option.enabled ? '#f8fafc' : '#f1f5f9',
                      opacity: option.enabled ? 1 : 0.65,
                      cursor: option.enabled ? 'pointer' : 'not-allowed'
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={checklistExportSelection.includes(option.id)}
                      disabled={!option.enabled || checklistExporting}
                      onChange={() => handleToggleChecklistExportOption(option.id)}
                      style={{ marginTop: '3px' }}
                    />
                    <div style={{ display: 'grid', gap: '4px' }}>
                      <div style={{ color: '#0f172a', fontWeight: '600' }}>{option.label}</div>
                      <div style={{ color: '#64748b', fontSize: '13px' }}>{countLabel}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
              <button
                type="button"
                onClick={handleExportChecklistPdf}
                disabled={checklistExporting}
                style={{ flex: 1, minWidth: '180px', padding: '10px 14px', borderRadius: '10px', border: 'none', backgroundColor: '#2563eb', color: 'white', cursor: checklistExporting ? 'progress' : 'pointer', fontWeight: '600' }}
              >
                {checklistExporting ? 'Exporting...' : 'Create PDF'}
              </button>
              <button
                type="button"
                onClick={() => setShowChecklistExportModal(false)}
                disabled={checklistExporting}
                style={{ flex: 1, minWidth: '180px', padding: '10px 14px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', color: '#0f172a', cursor: checklistExporting ? 'not-allowed' : 'pointer', fontWeight: '600' }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showAddContactModal ? (
        <div style={modalBackdropStyle}>
          <div style={modalCardStyle}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Add kontakt</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Name</label>
                <input
                  value={contactForm.name}
                  onChange={(event) => setContactForm((current) => ({ ...current, name: event.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Email</label>
                <input
                  value={contactForm.email}
                  onChange={(event) => setContactForm((current) => ({ ...current, email: event.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Phone</label>
                <input
                  value={contactForm.phone}
                  onChange={(event) => setContactForm((current) => ({ ...current, phone: event.target.value }))}
                  style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Notes</label>
                <textarea
                  value={contactForm.notes}
                  onChange={(event) => setContactForm((current) => ({ ...current, notes: event.target.value }))}
                  style={{ width: '100%', minHeight: '90px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', resize: 'vertical' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
              <button onClick={handleAddPropertyContact} disabled={savingContact} style={{ flex: 1, minWidth: '140px', padding: '10px', borderRadius: '10px', border: 'none', backgroundColor: '#10b981', color: 'white', cursor: 'pointer', fontWeight: '600' }}>{savingContact ? 'Saving...' : 'Save kontakt'}</button>
              <button onClick={() => setShowAddContactModal(false)} style={{ flex: 1, minWidth: '140px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      {showMarkSoldModal ? (
        <div style={modalBackdropStyle}>
          <div style={modalCardStyle}>
            <h3 style={{ marginTop: 0, color: '#0f172a' }}>Mark property as sold</h3>
            <div style={{ display: 'grid', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Price</label>
                <input value={markSoldForm.price} onChange={(event) => setMarkSoldForm((current) => ({ ...current, price: event.target.value }))} style={{ width: '100%', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }} />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Bruto provizija</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={markSoldForm.grossCommissionType === '%' ? markSoldForm.grossCommissionInput : markSoldForm.grossCommission} onChange={(event) => setMarkSoldForm((current) => current.grossCommissionType === '%' ? { ...current, grossCommissionInput: event.target.value } : { ...current, grossCommission: event.target.value })} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }} />
                  <select value={markSoldForm.grossCommissionType} onChange={(event) => setMarkSoldForm((current) => ({ ...current, grossCommissionType: event.target.value }))} style={{ width: '74px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Neto provizija</label>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <input value={markSoldForm.netCommissionType === '%' ? markSoldForm.netCommissionInput : markSoldForm.netCommission} onChange={(event) => setMarkSoldForm((current) => current.netCommissionType === '%' ? { ...current, netCommissionInput: event.target.value } : { ...current, netCommission: event.target.value })} style={{ flex: 1, padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }} />
                  <select value={markSoldForm.netCommissionType} onChange={(event) => setMarkSoldForm((current) => ({ ...current, netCommissionType: event.target.value }))} style={{ width: '74px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1' }}>
                    <option value="€">€</option>
                    <option value="%">%</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#64748b', display: 'block', marginBottom: '6px' }}>Buyers</label>
                <textarea value={markSoldForm.buyersText} onChange={(event) => setMarkSoldForm((current) => ({ ...current, buyersText: event.target.value }))} style={{ width: '100%', minHeight: '100px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', resize: 'vertical' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '10px', marginTop: '18px', flexWrap: 'wrap' }}>
              <button onClick={handleMarkSold} disabled={savingStatus} style={{ flex: 1, minWidth: '140px', padding: '10px', borderRadius: '10px', border: 'none', backgroundColor: '#16a34a', color: 'white', cursor: 'pointer', fontWeight: '600' }}>{savingStatus ? 'Saving...' : 'Confirm sold'}</button>
              <button onClick={() => setShowMarkSoldModal(false)} style={{ flex: 1, minWidth: '140px', padding: '10px', borderRadius: '10px', border: '1px solid #cbd5e1', backgroundColor: '#f8fafc', cursor: 'pointer', fontWeight: '600' }}>Cancel</button>
            </div>
          </div>
        </div>
      ) : null}

      {!hasMasterParcelChecklist ? (
        <UniversalChecklistModal
          key={`selling-property-checklist-${propertyForm?.id || 'none'}-${checklistTypeKey || 'none'}-${showChecklistModal ? 'open' : 'closed'}`}
          isOpen={showChecklistModal && Boolean(propertyForm?.id)}
          title={propertyForm ? `${propertyForm.katastrskaObcina} ${propertyForm.idParceleStavbe}`.trim() : 'Checklist'}
          typeLabel={checklistTypeKey}
          items={checklistTemplateItems}
          values={checklistValues}
          loading={checklistLoading}
          saving={checklistSaving}
          onClose={() => setShowChecklistModal(false)}
          onSaveValues={handleSaveChecklistValues}
          onSaveTemplate={handleSaveChecklistTemplate}
          onUploadDocument={handleChecklistDocumentUpload}
          onDownloadDocument={(value) => handleDocumentDownload(value?.documentPath, value?.documentName)}
          documentAccept={DOCUMENT_ACCEPT}
        />
      ) : null}
    </div>
  );
};

export default SellingPropertyPage;